import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Text,
  Divider,
  Tooltip,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Tab,
  TabList,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import { ArrowRepeatAllRegular, ArrowClockwiseRegular, StopRegular, ArrowDownloadRegular, BugRegular, DataTreemapRegular, PanelLeftRegular, PanelLeftFilled, HistoryRegular, ShieldLockRegular, GridDotsRegular } from '@fluentui/react-icons';
import { DesignerLayout } from './DesignerLayout';
import { WorkflowRunsPanel } from './WorkflowRunsPanel/WorkflowRunsPanel';
import { TriggerHistorySidebar } from '@/components/MonitoringView/TriggerHistorySidebar/TriggerHistorySidebar';
import { TriggerHistoryDetailPanel } from '@/components/MonitoringView/TriggerHistorySidebar/TriggerHistoryDetailPanel';
import { useMonitoringStore } from '@/stores/monitoringStore';
import { ResizablePanel } from './ResizablePanel';
import { WorkflowViewTabs } from './WorkflowViewTabs';
import { WorkflowDesigner } from './WorkflowDesigner';
import { LogsPanel } from '@/components/MonitoringView/BottomPanel/LogsPanel';
import { ChatLogPanel } from '@/components/MonitoringView/BottomPanel/ChatLogPanel';
import { ChatPanel } from '@/components/MonitoringView/BottomPanel/ChatPanel';
import { RunStatusBadge } from '@/components/MonitoringView/RunsSidebar/RunStatusBadge';
import { visuallyHiddenStyle } from '@/styles/a11y';
import { shouldSkipShortcut, matchesShortcut, isPrimaryMod } from '@/utils/input';
import { formatDateTime as canonicalFormatDateTime, formatDuration as canonicalFormatDuration } from '@/utils/dateTime';
import { ChatSidebar } from './ChatSidebar';
import { TriggerWorkflowDialog } from './TriggerWorkflowDialog';
import { SidePanel } from './SidePanel';
import { PaletteExtensions, type PaletteAction, type PaletteQuickAction } from '@/components/GlobalNavPalette';
import { ChatSidebarEscapeHandler } from './ChatSidebarEscapeHandler';
import { FlashRegular, AddRegular } from '@fluentui/react-icons';
import { paths } from '@/routes';
import { extractWorkflowName } from '@/types/logicApps';
import { useAgentChatAguiStore, type PendingPrompt } from '@/stores/agentChatAguiStore';
import { useUIPreferencesStore } from '@/stores/uiPreferencesStore';
import { ConnectorProvider } from '@/contexts';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useEditorRunStore } from '@/stores/editorRunStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useDraftModeStore, selectIsDraftMode } from '@/stores/draftModeStore';
import { transformExecutionToEditorState, canLoadIntoEditor, isAgentExecution } from '@/utils/monitoring';
import { useAppStore } from '@/stores/appStore';
import { openCreateWorkflowDialog } from '@/stores/createWorkflowDialogStore';
import { useAgentChatStore } from '@/stores/agentChatStore';
import { useStreamingRunStore } from '@/stores/streamingRunStore';
import { useAuth } from '@/auth/useAuth';
import { useAppPermission } from '@/hooks/useAppPermission';
import { useInfiniteRuns, useRun, useActionDetails, useResubmitRun, useCancelRun, useAllAgentIterations, useStatefulAgentIterations, useChatHistory, useAllActionsChatHistory, useAgentIterationActions, useAllRepetitions, useChatMessageIterationNodeExecutions, NODE_ID_PREFIX_CHATMSG_ACTION, useRepetitionScopeActions, RUN_SCOPED_QUERY_KEY_PREFIXES, useTriggerMockRun } from '@/hooks/useMonitoringData';
import { useQueryClient } from '@tanstack/react-query';
import { telemetryService } from '@/services/telemetryService';
import { useAgentUrl } from '@/hooks/useAgentUrl';
import { useRuntimeDetails } from '@/hooks/useAppDetails';
import { useSaveWorkflow } from '@/hooks/useSaveWorkflow';
import { useProactiveValidation } from '@/hooks/useProactiveValidation';
import { useDraftSave } from '@/hooks/useDraftSave';
import { getIsDraftDirty } from '@/hooks/useIsDraftDirty';
import { fetchRuns, canTriggerRunPublishedWorkflow } from '@/services/monitoringApi';
import type { AgentToolActionInfo, RepetitionInfo, BuiltInToolIterationInfo } from '@/services/monitoringApi';
import { buildFoundryPortalUrl } from '@/services/foundryAgentService';
import { getConnectorIndex, isIndexLoaded } from '@/services/connectorCatalog';
import type { AzureContext } from '@/types/agui';
import { useIsAgenticWorkflow } from '@/hooks/useIsAgenticWorkflow';
import { useStreamingRun } from '@/hooks/useStreamingRun';
import type { Run, Execution, NodeExecution, MonitoringMode } from '@/types/monitoring';
import type { PanelState } from './PanelHeader';
import { DataInspector } from './DataInspector';
import { parseAppId } from '@/types';
import { extractAzureRegion } from '@/services/logicAppsApi';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useNotificationStore } from '@/stores/notificationStore';
import { generateRecipeFromRun } from '@/data/recipes/recipeFromRun';
import type { Recipe } from '@/types/recipe';
import { CreateRecipeDialog } from './Templates/CreateRecipeDialog';
import { fetchRunWithAgentToolsWithOutputLinks, fetchActionContent } from '@/services/monitoringApi';
import type { WorkflowDefinition } from '@/types/workflowDefinition';

type ActiveFile = 'workflow.json' | 'connections.json';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  contentWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'hidden',
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  monitoringContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  runInfoHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    // Establish a size-containment context so children can adapt to the
    // toolbar's available width (not the viewport — the panel narrows
    // independently of the window).
    containerType: 'inline-size',
    containerName: 'run-info-header',
    // No horizontal scroll. When space is tight, the run ID truncates with
    // an ellipsis (full ID stays available via the tooltip), labels collapse
    // to icon-only, and the details segment hides. Anything that still does
    // not fit gets clipped rather than producing a horizontal scrollbar.
    overflow: 'hidden',
    minWidth: 0,
  },
  runInfoTitleWrap: {
    // The title is the most compressible element when space is tight.
    // Allow it to shrink (and truncate with an ellipsis) so the right-
    // aligned action buttons always stay visible without overlapping.
    // The Tooltip on the Text keeps the truncated ID discoverable.
    flexShrink: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  runInfoTitle: {
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  runInfoDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    // Never shrink in place — we'd end up clipping mid-character (e.g. "1s"
    // → "1") right next to the Refresh icon. Instead, hide the whole block
    // cleanly via the container query below when the toolbar gets tight.
    flexShrink: 0,
    // Hide details whenever the toolbar (not the viewport) is narrow
    // enough that the action cluster would otherwise compete for space.
    // The toolbar gets narrower when the run-list panel is open, so this
    // breakpoint reflects the toolbar's own width via
    // `containerType: inline-size` on `runInfoHeader`.
    '@container run-info-header (max-width: 1280px)': {
      display: 'none',
    },
  },
  canvasArea: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  canvasWithInspector: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  canvasMain: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  bottomPanelWrapper: {
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  bottomPanelContent: {
    display: 'flex',
    height: '100%',
    width: '100%',
  },
  // Chat panel - fixed width from ResizablePanel or collapsed width
  chatPanelWrapper: {
    height: '100%',
    display: 'flex',
    flexShrink: 0,
  },
  // Chat panel expanded to full width (when logs is collapsed)
  chatPanelWrapperExpanded: {
    height: '100%',
    display: 'flex',
    flex: 1,
    minWidth: 0,
  },
  // Logs panel - takes remaining space
  logsPanelWrapper: {
    flex: 1,
    height: '100%',
    minWidth: 0,
    display: 'flex',
  },
  // Logs panel collapsed - only takes collapsed bar width
  logsPanelWrapperCollapsed: {
    flex: 'none',
    height: '100%',
    display: 'flex',
  },
  // Designer with chat layout
  designerWithChat: {
    display: 'flex',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  designerContent: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
  },
  dividerHeight: {
    height: '20px',
  },
  runInfoActions: {
    // Grow to absorb extra horizontal space so the action cluster
    // right-aligns regardless of how short the run-ID column is.
    flex: '1 1 auto',
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: 0,
    // Lock direct children so labels never wrap or get character-clipped.
    '& > button': {
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
  },
  // Right-side cluster that holds Debug / Export / Inspector / Rerun. All
  // actions render inline at every width — labels collapse to icon-only
  // at <1100px via the `toolbarButtonLabel` container query. No More
  // menu, no overflow logic.
  runInfoOverflowGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
    flexShrink: 0,
    '& > *': {
      flexShrink: 0,
    },
  },
  dividerInlineMargin: {
    marginInline: tokens.spacingHorizontalXS,
  },
  // Stable-width slot for the relative-time label. A hidden "ghost" copy of
  // the worst-case text reserves the slot width, and the live label sits on
  // top via grid stacking. The slot stays the same width regardless of the
  // current label content (no jitter on second/minute transitions).
  refreshTimestampSlot: {
    display: 'inline-grid',
    '@container run-info-header (max-width: 1000px)': {
      display: 'none',
    },
  },
  refreshTimestampGhost: {
    gridArea: '1 / 1',
    visibility: 'hidden',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  refreshTimestamp: {
    gridArea: '1 / 1',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
  // Action buttons collapse to icon-only at <1100px. Their tooltip
  // preserves the label, but the button itself stays inline at every
  // width — there is no More menu. Refresh keeps its label down to
  // 820px because it's the most-used action.
  toolbarButtonLabel: {
    '@container run-info-header (max-width: 1100px)': {
      display: 'none',
    },
  },
  // Refresh-button label has its own slot so it stays visible at mid widths
  // even when other action labels collapse.
  refreshButtonLabel: {
    '@container run-info-header (max-width: 820px)': {
      display: 'none',
    },
  },
  // Status badge has two variants: the labelled pill at comfortable widths
  // and a colored dot at narrow widths (where the small filled pill clips
  // its label and renders unreadable). Same color semantics, less noise.
  statusBadgeFull: {
    display: 'inline-flex',
    alignItems: 'center',
    '@container run-info-header (max-width: 820px)': {
      display: 'none',
    },
  },
  statusBadgeCompact: {
    display: 'none',
    alignItems: 'center',
    '@container run-info-header (max-width: 820px)': {
      display: 'inline-flex',
    },
  },
  visuallyHidden: visuallyHiddenStyle,
  monitoringLayout: {
    display: 'flex',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  runListSidebar: {
    width: '320px',
    minWidth: '320px',
    height: '100%',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  monitoringSubTabs: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  monitoringSubTabBody: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  monitoringMain: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  monitoringReadOnlyBanner: {
    flexShrink: 0,
  },
  readOnlyBannerIcon: {
    marginRight: tokens.spacingHorizontalSNudge,
    verticalAlign: 'text-bottom',
  },
  monitoringEmptyDetail: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
  },
  monitoringEmptyIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeHero900,
  },
  monitoringEmptyText: {
    color: tokens.colorNeutralForeground3,
  },
  monitoringEmptyActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

/** Descriptor for an action in the run-info toolbar. Each action renders
 *  inline as a Button — its label collapses to icon-only at narrow widths
 *  via the `toolbarButtonLabel` container query. There is no overflow
 *  menu: every action stays reachable in the toolbar at every width. */
type RunAction = {
  id: string;
  label: string;
  ariaLabel: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  appearance?: 'subtle' | 'primary';
  tooltip?: string;
};

type WorkflowDesignerContentProps = {
  initialRunId?: string | undefined;
  /** Initial debug run ID - loads execution into editor */
  initialDebugRunId?: string | undefined;
};

const WorkflowDesignerContent = ({ initialRunId, initialDebugRunId }: WorkflowDesignerContentProps) => {
  const styles = useStyles();
  const { workflowName, locationName, projectName, appName, tenantId } = useParams<{
    workflowName: string;
    locationName: string;
    projectName: string;
    appName: string;
    tenantId: string;
  }>();
  // Stable identity across the full route hierarchy. Two workflows in different
  // apps can share a name, so keying remounts on workflowName alone would leak
  // component-local state across app/project/tenant switches. Mirrors the key
  // used in WorkflowDesignerPage's route-identity cleanup effect.
  const routeIdentityKey = `${tenantId ?? ''}/${locationName ?? ''}/${projectName ?? ''}/${appName ?? ''}/${workflowName ?? ''}`;
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit: hasEditPermission, isLoading } = useAppPermission();
  const monitoringMode = useMonitoringStore((s) => s.monitoringMode);
  const setMonitoringMode = useMonitoringStore((s) => s.setMonitoringMode);
  const handleMonitoringTabSelect = useCallback(
    (_: SelectTabEvent, data: SelectTabData) => {
      setMonitoringMode(data.value as MonitoringMode);
    },
    [setMonitoringMode]
  );
  const selectedTriggerHistoryId = useMonitoringStore((s) => s.selectedTriggerHistoryId);
  const setSelectedTriggerHistoryId = useMonitoringStore((s) => s.setSelectedTriggerHistoryId);
  const isRunsRoute = /\/runs(\/|$)/.test(location.pathname);
  const activeTab: 'designer' | 'monitoring' = isRunsRoute ? 'monitoring' : 'designer';
  // Base path up to the workflow name (strips /runs and /runs/:runId)
  const workflowBasePath = location.pathname.replace(/\/runs(\/.*)?$/, '');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(initialRunId ?? null);

  // Sync selectedRunId from URL when route changes (e.g., browser back/forward)
  useEffect(() => {
    const match = location.pathname.match(/\/runs\/([^/]+)/);
    let urlRunId: string | null = null;
    try {
      urlRunId = match?.[1] ? decodeURIComponent(match[1]) : null;
    } catch {
      // Malformed percent-encoding in URL — ignore
    }
    if (urlRunId && urlRunId !== selectedRunId) {
      setSelectedRunId(urlRunId);
      // Reset dependent state when switching to a different run
      setSelectedExecutionId(null);
      setSelectedNodeId(null);
      setSelectedToolIteration(null);
      setAgentIterations({});
      setScopeIterations({});
      setToolActions(new Map());
      setPendingToolActionFetches(new Set());
      setToolActionFetchQueue([]);
      setToolActionFetchRequest(null);
      setScopeRepetitions(new Map());
      setPendingScopeRepetitionFetches(new Set());
      setSelectedScopeRepetitions({});
      setScopeRepetitionFetchRequest(null);
    } else if (!urlRunId && isRunsRoute) {
      // On /runs without a run ID (e.g., user clicked back) - clear selection.
      // We intentionally do NOT force the run list open here; the user's
      // persisted runListOpen preference wins. The empty-state header keeps
      // the same run-list toggle visible so the list can be reopened.
      setSelectedRunId(null);
      setSelectedExecutionId(null);
      setSelectedNodeId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // Track the iteration index for the selected tool (used for looking up tool action data)
  const [selectedToolIteration, setSelectedToolIteration] = useState<number | null>(null);
  const [isTriggerDialogOpen, setIsTriggerDialogOpen] = useState(false);
  // Run history list expand/collapse — persisted via uiPreferencesStore so
  // the user's preference survives tab switches, workflow navigation, and
  // reloads. 
  const isRunListOpen = useUIPreferencesStore((s) => s.runListOpen);
  const toggleRunList = useUIPreferencesStore((s) => s.toggleRunList);
  // Single source of truth for the run-list toggle. Rendered in two places
  // (run-info header and empty-state header) so the affordance is reachable
  // whether or not a run is currently selected.
  const runListToggleLabel = isRunListOpen ? 'Hide run list' : 'Show run list';
  const runListToggleButton = (
    <Tooltip content={runListToggleLabel} relationship="label">
      <Button
        appearance="subtle"
        icon={isRunListOpen ? <PanelLeftFilled /> : <PanelLeftRegular />}
        onClick={toggleRunList}
        aria-label={runListToggleLabel}
        size="small"
      />
    </Tooltip>
  );
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const handleTriggerSuccessRef = useRef<(runId?: string) => void>(() => {});

  // Independent panel states - Code and Assistant can both be open simultaneously
  const [codePanelState, setCodePanelState] = useState<PanelState>('minimized');
  const [isChatSidebarOpen, setIsChatSidebarOpen] = useState(false);

  // Whether the assistant panel is minimized to a rail.
  // Defaults to `true` so the right-hand rail is visible on first load — this
  // surfaces Copilot in a familiar location even before the user discovers
  // the toggle on the floating command bar.
  const [isAssistantMinimized, setIsAssistantMinimized] = useState(true);

  // Derive canvas state from code panel state
  const canvasPanelState: PanelState = codePanelState === 'maximized' ? 'minimized' : codePanelState === 'minimized' ? 'maximized' : 'normal';

  // Collapse states for monitoring bottom panels
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [isLogsCollapsed, setIsLogsCollapsed] = useState(false);

  // Data Inspector state
  const [isInspectorOpen, setIsInspectorOpen] = useState(false);

  // Agent iteration state for monitoring mode (tracks current iteration per agent)
  const [agentIterations, setAgentIterations] = useState<Record<string, number>>({});

  // Scope iteration state for monitoring mode (tracks current iteration per scope node)
  const [scopeIterations, setScopeIterations] = useState<Record<string, number>>({});

  // Tool actions state - stores fetched tool actions by iteration key (agentName-iterationName)
  const [toolActions, setToolActions] = useState<Map<string, AgentToolActionInfo[]>>(new Map());
  // Track which iterations are currently being fetched
  const [pendingToolActionFetches, setPendingToolActionFetches] = useState<Set<string>>(new Set());
  // Queue of tool action fetch requests (to handle multiple concurrent fetches)
  const [toolActionFetchQueue, setToolActionFetchQueue] = useState<Array<{
    agentName: string;
    iterationName: string;
  }>>([]);
  // Current fetch request being processed
  const [toolActionFetchRequest, setToolActionFetchRequest] = useState<{
    agentName: string;
    iterationName: string;
  } | null>(null);

  // Scope repetition state - stores fetched repetitions by scope name
  const [scopeRepetitions, setScopeRepetitions] = useState<Map<string, RepetitionInfo[]>>(new Map());
  // Ref mirror for scopeRepetitions so callbacks can read the latest value
  // synchronously without depending on state inside their useCallback deps.
  // This avoids running side effects from inside a setState updater (which
  // React StrictMode may invoke twice).
  const scopeRepetitionsRef = useRef(scopeRepetitions);
  useEffect(() => {
    scopeRepetitionsRef.current = scopeRepetitions;
  }, [scopeRepetitions]);
  // Track which scopes are currently being fetched
  const [pendingScopeRepetitionFetches, setPendingScopeRepetitionFetches] = useState<Set<string>>(new Set());
  // Currently selected scope repetition (scopeName -> repetitionName)
  const [selectedScopeRepetitions, setSelectedScopeRepetitions] = useState<Record<string, string>>({});
  // Current scope fetch request
  const [scopeRepetitionFetchRequest, setScopeRepetitionFetchRequest] = useState<string | null>(null);

  // Scope actions state - stores fetched actions by key (scopeName-repetitionName)
  const [scopeActions, setScopeActions] = useState<Map<string, NodeExecution[]>>(new Map());
  // Track which scope-repetition pairs are currently being fetched
  const [pendingScopeActionFetches, setPendingScopeActionFetches] = useState<Set<string>>(new Set());
  // Synchronous shadow of pendingScopeActionFetches. The state Set is a stale
  // closure value within rapid double calls (e.g. React StrictMode's double
  // effect), so we use a ref for synchronous "is this key already enqueued?"
  // checks. The state Set is still kept for render-time reads (loading badges).
  const pendingScopeActionFetchesRef = useRef<Set<string>>(new Set());
  // Current scope action fetch request
  const [scopeActionFetchRequest, setScopeActionFetchRequest] = useState<{ scopeName: string; repetitionName: string } | null>(null);
  // Queue of scope action fetch requests
  const [scopeActionFetchQueue, setScopeActionFetchQueue] = useState<Array<{ scopeName: string; repetitionName: string }>>([]); 

  // ChatMessage iteration actions state - stores fetched NodeExecutions by iteration index
  // Used for OMT-based chatMessage workflows where each turn has stateful-like actions
  // Now uses NodeExecution[] - the SAME type as stateful workflows!
  // NOTE: Non-OMT Agent workflows (kind: Agent with A2A) use agentIterations/toolActions instead.
  const [chatMessageIterationActions, setChatMessageIterationActions] = useState<Map<number, NodeExecution[]>>(new Map());
  // Track which iterations are currently being fetched
  const [pendingChatMessageIterationFetches, setPendingChatMessageIterationFetches] = useState<Set<number>>(new Set());
  // Current fetch request being processed
  const [chatMessageIterationFetchRequest, setChatMessageIterationFetchRequest] = useState<number | null>(null);

  // Handler for agent iteration changes from the canvas
  const handleIterationChange = useCallback((agentName: string, iteration: number) => {
    setAgentIterations(prev => ({
      ...prev,
      [agentName]: iteration,
    }));
    // Also sync the selection state for LogsPanel highlighting
    // Set both the selected node (agent) and iteration index
    setSelectedNodeId(`action-${agentName}`);
    setSelectedToolIteration(iteration);
  }, []);

  // Handler for scope iteration changes from the canvas (foreach, until nodes)
  // Updates canvas state AND syncs the side panel's selected repetition so the
  // detail pane reflects whatever iteration the user is scrubbing through.
  const handleScopeIterationChange = useCallback((scopeName: string, iteration: number) => {
    setScopeIterations(prev => ({
      ...prev,
      [scopeName]: iteration,
    }));
    const reps = scopeRepetitionsRef.current.get(scopeName);
    const target = reps?.[iteration];
    if (target) {
      setSelectedScopeRepetitions(repPrev => ({
        ...repPrev,
        [scopeName]: target.name,
      }));
    }
  }, []);

  // Handlers that prevent both panels from being collapsed at the same time
  const handleChatCollapseChange = useCallback((collapsed: boolean) => {
    if (collapsed && isLogsCollapsed) {
      // If trying to collapse chat while logs is collapsed, expand logs first
      setIsLogsCollapsed(false);
    }
    setIsChatCollapsed(collapsed);
  }, [isLogsCollapsed]);

  const handleLogsCollapseChange = useCallback((collapsed: boolean) => {
    if (collapsed && isChatCollapsed) {
      // If trying to collapse logs while chat is collapsed, expand chat first
      setIsChatCollapsed(false);
    }
    setIsLogsCollapsed(collapsed);
  }, [isChatCollapsed]);

  const { activeFile, setActiveFile, connections, workflow, selectedNode, selectedNodes } = useWorkflowStore();

  // Effective selection for context-aware UI (command palette, quick actions).
  // Canvas clicks use `openNodeEditor`, which only sets `selectedNode` and leaves
  // `selectedNodes` empty so multi-selection is preserved. Treat the editor's
  // open node as a single-selection fallback for context surfaces.
  const contextSelectedNodes = useMemo(
    () => (selectedNodes.length > 0 ? selectedNodes : selectedNode ? [selectedNode] : []),
    [selectedNodes, selectedNode],
  );
  const { selectedApp } = useAppStore();
  const { getOttoToken, account } = useAuth();
  const isGenerateTemplateEnabled = useFeatureFlag('generateTemplate');
  const addNotification = useNotificationStore((s) => s.addNotification);
  const [isGeneratingRecipe, setIsGeneratingRecipe] = useState(false);
  // Recipe generated from the selected run — non-null opens the create-recipe
  // dialog (metadata → sanitize → download).
  const [generatedRecipe, setGeneratedRecipe] = useState<Recipe | null>(null);
  const effectiveAppId = useMemo(() => selectedApp?.armResourceId ?? selectedApp?.properties?.appResourceId ?? null, [selectedApp]);
  const { data: appDetails } = useRuntimeDetails();
  const parsedResource = useMemo(() => parseAppId(effectiveAppId ?? ''), [effectiveAppId]);

  // Editor run store for debug mode
  const loadExecution = useEditorRunStore((state) => state.loadExecution);
  const clearExecution = useEditorRunStore((state) => state.clearExecution);
  const connectionReferences = useConnectionStore((state) => state.references);

  // Agent chat store for workflow generation from canvas
  const { openSidebar, sendMessage, clearChat, setHasUnreadMessages, clearPendingPrompt } = useAgentChatAguiStore();
  const isStreaming = useAgentChatAguiStore((state) => state.isStreaming);
  const hasUnreadMessages = useAgentChatAguiStore((state) => state.hasUnreadMessages);
  const pendingPrompt = useAgentChatAguiStore((state) => state.pendingPrompt);
  const prevIsStreamingRef = useRef(isStreaming);

  // Tracks whether we're waiting for a streaming run to produce a real runId
  // so we can auto-navigate to the run monitoring view.
  const pendingStreamingNavRef = useRef(false);

  // Quick actions for command palette — context-aware based on selected nodes
  const quickActions = useMemo((): PaletteQuickAction[] => {
    const actions: PaletteQuickAction[] = [];

    if (contextSelectedNodes.length === 1) {
      const nodeName = contextSelectedNodes[0]!.name;
      actions.push(
        { id: 'configure-node', label: `Configure "${nodeName}" parameters`, prompt: `Help me configure the "${nodeName}" action parameters` },
        { id: 'error-handling-node', label: `Add error handling to "${nodeName}"`, prompt: `Add error handling around the "${nodeName}" action to handle failures gracefully` },
        { id: 'explain-node', label: `Explain "${nodeName}"`, prompt: `Explain what the "${nodeName}" action does and how it's configured` },
      );
    } else if (contextSelectedNodes.length > 1) {
      const names = contextSelectedNodes.map(n => n.name).join(', ');
      actions.push(
        { id: 'explain-selected', label: `Explain selected actions`, prompt: `Explain what these actions do: ${names}` },
        { id: 'error-handling-selected', label: `Add error handling to selected`, prompt: `Add error handling around these actions: ${names}` },
      );
    }

    // Always include workflow-level actions
    actions.push(
      { id: 'error-handling', label: 'Add error handling', prompt: 'Add error handling to handle failures gracefully' },
      { id: 'retry-logic', label: 'Add retry logic', prompt: 'Add retry logic with exponential backoff' },
      { id: 'logging', label: 'Add logging', prompt: 'Add logging to track execution' },
      { id: 'explain', label: 'Explain this workflow', prompt: 'Explain what this workflow does step by step' },
    );

    return actions;
  }, [contextSelectedNodes]);

  // Ask AI bundle — memoized so the object identity is stable across
  // renders. The Register effect only re-runs on `resetOnDeps`, so an
  // unstable askAi reference doesn't republish today; memoizing makes
  // it safe for future maintainers who may add askAi to resetOnDeps.
  const askAi = useMemo(
    () => ({
      items: quickActions,
      subtitle: contextSelectedNodes.length > 0 ? 'about your selection' : 'about this workflow',
    }),
    [quickActions, contextSelectedNodes.length],
  );

  // Handler for command palette message send
  const handleCommandPaletteMessage = useCallback((message: string) => {
    if (!workflowName) return;

    const azureContext: AzureContext = {
      subscriptionId: parsedResource?.subscriptionId ?? 'local-subscription',
      resourceGroup: parsedResource?.resourceGroup ?? 'local-resource-group',
      siteName: parsedResource?.appName ?? 'local-runtime',
      location: extractAzureRegion(appDetails?.hostName ?? '') ?? 'local',
      workflowName,
      hostName: appDetails?.hostName ?? '',
    };

    // Open the assistant panel to show the response
    setIsChatSidebarOpen(true);
    setIsAssistantMinimized(false);
    sendMessage(message, getOttoToken, azureContext, undefined);
  }, [workflowName, parsedResource?.subscriptionId, parsedResource?.resourceGroup, parsedResource?.appName, appDetails?.hostName, sendMessage, getOttoToken]);

  // Clear chat state when switching workflows to prevent cross-workflow contamination
  useEffect(() => {
    clearChat();
  }, [workflowName, clearChat]);

  // Handler to toggle chat sidebar - independent of code panel
  const handleToggleChatSidebar = useCallback(() => {
    if (isChatSidebarOpen) {
      setIsChatSidebarOpen(false);
    } else {
      // Opening the assistant panel
      setIsChatSidebarOpen(true);
      setIsAssistantMinimized(false); // Expand if minimized
      setHasUnreadMessages(false); // Clear unread badge
    }
  }, [isChatSidebarOpen, setHasUnreadMessages]);

  // Lazily prime the connector catalog the first time the assistant panel
  // opens. Only the assistant's DiscoverConnectors tool reads this index, so
  // there's no value in fetching it on every workflow mount.
  useEffect(() => {
    if (!isChatSidebarOpen) return;
    const projectName = selectedApp?.projectName;
    if (!projectName) return;
    if (isIndexLoaded(projectName)) return;

    let cancelled = false;
    getOttoToken()
      .then((token) => {
        if (cancelled || !token) return;
        return getConnectorIndex(token, projectName);
      })
      .catch((err) => {
        console.warn('[WorkflowDesignerWithCode] Connector index prefetch failed:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [isChatSidebarOpen, selectedApp?.projectName, getOttoToken]);

  // Auto-open assistant panel when streaming completes (was streaming, now stopped)
  useEffect(() => {
    if (prevIsStreamingRef.current && !isStreaming) {
      // Streaming just stopped
      if (isAssistantMinimized) {
        // Panel is minimized - show badge instead of opening
        setHasUnreadMessages(true);
      } else if (!isChatSidebarOpen) {
        // Panel is closed - show badge
        setHasUnreadMessages(true);
      }
      // If panel is open, user can already see the results
    }
    prevIsStreamingRef.current = isStreaming;
  }, [isStreaming, isAssistantMinimized, isChatSidebarOpen, setHasUnreadMessages]);

  // Consume pending prompts from AI entry points (picker, node editor, workflow-list-hero, etc.)
  useEffect(() => {
    // Fallback: hydrate from sessionStorage if Zustand has no pending prompt (page refresh scenario)
    let prompt = pendingPrompt;
    if (!prompt) {
      try {
        const raw = sessionStorage.getItem('otto:pendingPrompt');
        if (raw) {
          const stored = JSON.parse(raw) as PendingPrompt & { storedAt?: number };
          const age = Date.now() - (stored.storedAt ?? 0);
          if (age < 60_000) {
            prompt = { content: stored.content, scope: stored.scope, source: stored.source, systemContext: stored.systemContext };
          }
          sessionStorage.removeItem('otto:pendingPrompt');
        }
      } catch { /* sessionStorage unavailable */ }
    }

    if (!prompt) return;
    if (isStreaming) {
      clearPendingPrompt();
      return;
    }
    if (!workflowName) {
      setIsChatSidebarOpen(true);
      setIsAssistantMinimized(false);
      clearPendingPrompt();
      return;
    }

    const azureContext: AzureContext = {
      subscriptionId: parsedResource?.subscriptionId ?? 'local-subscription',
      resourceGroup: parsedResource?.resourceGroup ?? 'local-resource-group',
      siteName: parsedResource?.appName ?? 'local-runtime',
      location: extractAzureRegion(appDetails?.hostName ?? '') ?? 'local',
      workflowName,
      hostName: appDetails?.hostName ?? '',
    };

    setIsChatSidebarOpen(true);
    setIsAssistantMinimized(false);
    setHasUnreadMessages(false);
    sendMessage(prompt.content, getOttoToken, azureContext, prompt.scope);
    clearPendingPrompt();
  }, [pendingPrompt, parsedResource?.subscriptionId, parsedResource?.resourceGroup, parsedResource?.appName, appDetails?.hostName, workflowName, isStreaming, sendMessage, getOttoToken, setHasUnreadMessages, clearPendingPrompt]);

  // Save workflow functionality (includes validation before save)
  const { save, isSaving, isDirty, isValidating: isSaveValidating } = useSaveWorkflow({
    workflowName: workflowName ?? '',
    onSuccess: () => {
      console.log('[WorkflowDesignerWithCode] Workflow saved successfully');
    },
    onError: (error) => {
      console.error('[WorkflowDesignerWithCode] Failed to save workflow:', error);
    },
    onValidationFailed: (result) => {
      console.warn('[WorkflowDesignerWithCode] Validation failed, save blocked:', result);
    },
  });

  // Proactive validation - runs in background as user edits workflow
  const {
    isValidating: isProactiveValidating,
    errorCount: validationErrorCount,
    triggerValidation,
  } = useProactiveValidation({
    workflowName: workflowName ?? '',
    enabled: activeTab === 'designer' && !!workflowName,
  });

  // Draft mode state
  const isDraftMode = useDraftModeStore(selectIsDraftMode);

  // Streaming run support — provides streaming draft trigger for TriggerWorkflowDialog
  const { handleStreamingDraftTrigger } = useStreamingRun();
  const triggerMockRunMutation = useTriggerMockRun();
  
  // Draft save for running draft (saves immediately before run)
  const { save: saveDraft, isSaving: isDraftSaving } = useDraftSave({
    workflowName: workflowName ?? '',
  });
  
  // Callback to save draft before running
  const handleSaveDraft = useCallback(async (): Promise<boolean> => {
    try {
      const result = await saveDraft();
      return result.success;
    } catch {
      return false;
    }
  }, [saveDraft]);

  // Keyboard shortcut: Ctrl+S / Cmd+S to save
  //
  // In draft mode, route to `saveDraft()` so users in non-instant autosave
  // modes (`30s` / `5min` / `off`) can commit pending changes on demand.
  // Outside draft mode, fall through to the publish-flow `save()` (which
  // performs validation before persisting).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (shouldSkipShortcut(e)) return;
      // Platform-correct save chord: ⌘S on Mac, Ctrl+S on Win/Linux.
      // Sourced from the central shortcuts registry so the keystroke,
      // tooltip label, and help dialog stay in sync.
      if (matchesShortcut(e, 'save')) {
        e.preventDefault();
        if (isDraftMode) {
          // Skip when nothing is pending or a save is already in flight —
          // mashing Ctrl/Cmd+S should not enqueue duplicate writes. Read
          // dirty flags via the non-reactive helper so this effect doesn't
          // re-bind every keystroke (it already depends on `isDraftSaving`).
          if (isDraftSaving || !getIsDraftDirty()) return;
          void saveDraft();
          return;
        }
        if (isDirty && !isSaving && !isSaveValidating) {
          save();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDirty, isSaving, isSaveValidating, save, isDraftMode, saveDraft, isDraftSaving]);

  // Handler for generating workflow from empty canvas textarea
  const handleGenerateWorkflow = useCallback((prompt: string) => {
    if (!workflowName) {
      console.error('[WorkflowDesignerWithCode] Cannot generate: missing workflow name');
      return;
    }

    // Don't open sidebar immediately - let the canvas show skeleton state
    // Sidebar will open when user explicitly toggles it or when needed
    openSidebar(); // Initialize the chat store state

    // Build Azure context — use fallbacks for local/non-ARM environments
    // (matches the pattern used by handleCommandPaletteMessage and ChatSidebar)
    const azureContext: AzureContext = {
      subscriptionId: parsedResource?.subscriptionId ?? 'local-subscription',
      resourceGroup: parsedResource?.resourceGroup ?? 'local-resource-group',
      siteName: parsedResource?.appName ?? 'local-runtime',
      location: extractAzureRegion(appDetails?.hostName ?? '') ?? 'local',
      workflowName,
      hostName: appDetails?.hostName ?? '',
    };

    sendMessage(prompt, getOttoToken, azureContext, undefined);
  }, [parsedResource, workflowName, openSidebar, appDetails?.hostName, sendMessage, getOttoToken]);

  // Handler to toggle code panel visibility - independent of assistant
  const handleToggleCode = useCallback(() => {
    setCodePanelState(prev => prev === 'minimized' ? 'normal' : 'minimized');
  }, []);

  // Handler to close the assistant panel
  const handleCloseAssistant = useCallback(() => {
    setIsChatSidebarOpen(false);
  }, []);

  // Handler to minimize the assistant panel to a rail
  const handleMinimizeAssistant = useCallback(() => {
    setIsAssistantMinimized(true);
  }, []);

  // Handler to expand the assistant from rail
  const handleExpandAssistant = useCallback(() => {
    setIsChatSidebarOpen(true);
    setIsAssistantMinimized(false);
    setHasUnreadMessages(false);
  }, [setHasUnreadMessages]);

  // Keyboard shortcuts for panel navigation
  useEffect(() => {
    if (activeTab !== 'designer') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (shouldSkipShortcut(e)) return;

      // Platform-aware primary modifier (⌘ on Mac, Ctrl elsewhere).
      // Rejects the opposite modifier so e.g. Ctrl+E on Mac no longer
      // accidentally toggles the code panel.
      const isMod = isPrimaryMod(e);

      // Ctrl+E / Cmd+E: Toggle Code panel
      if (isMod && !e.shiftKey && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        if (e.repeat) return;
        handleToggleCode();
      }

      // Cmd+Shift+E: Toggle Code panel (alternative)
      if (isMod && e.shiftKey && e.key === 'E') {
        e.preventDefault();
        if (e.repeat) return;
        handleToggleCode();
      }

      // Cmd+Shift+A: Toggle AI/Assistant panel
      if (isMod && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        if (e.repeat) return;
        handleToggleChatSidebar();
      }

      // Ctrl+Enter / Cmd+Enter: Run workflow
      if (isMod && e.key === 'Enter') {
        e.preventDefault();
        if (e.repeat) return;
        setIsTriggerDialogOpen(true);
      }

      // Esc-to-close-assistant lives in <ChatSidebarEscapeHandler /> so the
      // palette-open subscription doesn't re-render this entire subtree.
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, handleToggleCode, handleToggleChatSidebar]);

  // Detect workflow type for chat panel behavior:
  // - isConversational: workflow has kind: "Agent" (conversational agent - supports live chat)
  // - hasAgentAction: workflow has Agent actions but not kind: "Agent" (autonomous agent)
  const { isConversational, hasAgentAction } = useIsAgenticWorkflow({
    workflowName: workflowName ?? undefined,
    isDraft: isDraftMode,
  });

  // Either type of agent workflow shows chat panel in monitoring mode
  const isAnyAgentWorkflow = isConversational || hasAgentAction;

  // Get agent URL config for conversational agents (provides agentUrl, apiKey, oboUserToken)
  const { data: agentUrlConfig } = useAgentUrl({
    subscriptionId: parsedResource?.subscriptionId ?? '',
    resourceGroup: parsedResource?.resourceGroup ?? '',
    appName: parsedResource?.appName ?? '',
    workflowName: workflowName ?? '',
    hostName: appDetails?.hostName ?? '',
    enabled: isConversational,
  });

  // Use the same infinite runs query as WorkflowRunsPanel — React Query deduplicates
  // by query key so both this component and the panel share a single API call.
  // Only enabled on the monitoring tab to avoid background polling on the designer tab.
  const { allRuns: unfilteredRuns } = useInfiniteRuns({ enabled: activeTab === 'monitoring' });

  // Fetch full run details when a run is selected (includes expanded actions)
  const { data: fullRunData, isLoading: isRunLoading, dataUpdatedAt: runDataUpdatedAt } = useRun(selectedRunId);

  // Fetch debug run data when initialDebugRunId is provided (for debug mode from URL)
  const { data: debugRunData } = useRun(initialDebugRunId ?? null);

  // Effect to load debug run into editor when data is available
  useEffect(() => {
    if (!initialDebugRunId || !debugRunData || !workflow) return;

    // Check if we can load this run into the editor
    if (!canLoadIntoEditor(debugRunData)) return;

    // Get the default execution
    const execution = debugRunData.executions?.[0];
    if (!execution) return;

    // Transform and load into editor store
    const editorRunState = transformExecutionToEditorState(debugRunData, execution, {
      autoPinTrigger: true,
      originalWorkflow: workflow,
    });
    loadExecution(editorRunState);
  }, [initialDebugRunId, debugRunData, workflow, loadExecution]);

  // Fetch action/trigger input/output details when a node is selected
  // For agent workflows, the trigger details API returns 404, so we skip it
  const { data: actionDetails, isLoading: isActionDetailsLoading } = useActionDetails(
    selectedRunId,
    selectedNodeId,
    { isAgenticWorkflow: isAnyAgentWorkflow }
  );

  // Resubmit and cancel run mutations
  const resubmitMutation = useResubmitRun();
  const cancelMutation = useCancelRun();

  // Get the selected run from full run data
  const selectedRun = useMemo(() => {
    if (fullRunData) return fullRunData;
    if (!unfilteredRuns || !selectedRunId) return null;
    return unfilteredRuns.find((r: Run) => r.id === selectedRunId) ?? null;
  }, [fullRunData, unfilteredRuns, selectedRunId]);

  const selectedExecution = useMemo(() => {
    if (!selectedRun || !selectedExecutionId) return null;
    return selectedRun.executions?.find((e: Execution) => e.id === selectedExecutionId) ?? null;
  }, [selectedRun, selectedExecutionId]);

  // Get base node executions from run data
  const baseNodeExecutions = useMemo((): NodeExecution[] => {
    if (selectedExecution) {
      return selectedExecution.nodeExecutions;
    } else if (selectedRun?.executions?.[0]) {
      return selectedRun.executions[0].nodeExecutions;
    }
    return [];
  }, [selectedRun, selectedExecution]);

  // Identify agent nodes from the run to proactively fetch their iterations
  const agentNodeNames = useMemo(() => {
    return baseNodeExecutions
      .filter((exec) => isAgentExecution(exec))
      .map((exec) => exec.nodeName);
  }, [baseNodeExecutions]);

  // Detect chatMessage workflows (OMT-based) by checking for the internal orchestrator
  // These workflows use 'when_a_chat_message_is_received' trigger and need chat history
  // for multi-turn conversation grouping in the execution log
  const isChatMessageWorkflow = useMemo(() => {
    return baseNodeExecutions.some(
      (exec) => exec.nodeName === 'internal.MessageIterationOrchestrator'
    );
  }, [baseNodeExecutions]);

  // Proactively fetch iterations for ALL agent nodes when a run is selected
  // Use different APIs based on workflow kind:
  // - Agent kind (isConversational): Timeline API returns all iterations in a single call
  // - Stateful with Agent actions: AgentRepetitions API (one call per agent)

  // For Agent kind workflows (A2A), use Timeline API
  const { data: timelineIterationsData, isLoading: isTimelineLoading } = useAllAgentIterations(
    selectedRunId,
    agentNodeNames,
    { enabled: isConversational }
  );

  // For Stateful workflows with Agent actions, use AgentRepetitions API
  const { data: statefulIterationsData, isLoading: isStatefulLoading } = useStatefulAgentIterations(
    selectedRunId,
    agentNodeNames,
    { enabled: !isConversational && agentNodeNames.length > 0 }
  );

  // Combine iteration data from the appropriate source
  const agentIterationsData = isConversational ? timelineIterationsData : statefulIterationsData;
  const isAgentIterationsLoading = isConversational ? isTimelineLoading : isStatefulLoading;

  // Extract all tool names from execution log for determining which tools are clickable in chat log
  const executionLogToolNames = useMemo(() => {
    const toolNames = new Set<string>();
    if (agentIterationsData) {
      for (const iterations of agentIterationsData.values()) {
        for (const iteration of iterations) {
          for (const tool of iteration.tools) {
            toolNames.add(tool.toolName);
          }
        }
      }
    }
    return toolNames;
  }, [agentIterationsData]);

  // Fetch chat history for the selected run
  // Different APIs based on workflow type:
  // - A2A/Conversational (kind: Agent): useChatHistory (run-level API)
  // - ChatMessage/OMT workflows: useChatHistory (run-level API)
  // - Stateful with Agent action: useActionChatHistory (action-level API)

  // For A2A and chatMessage workflows, use run-level chat history
  const { data: runLevelChatHistory } = useChatHistory(selectedRunId, {
    enabled: (isConversational || isChatMessageWorkflow) && !!selectedRunId,
  });

  // For Stateful workflows with Agent actions, use action-level chat history
  // Fetch chat history for all agent actions and combine them
  const { data: actionLevelChatHistory } = useAllActionsChatHistory(
    selectedRunId,
    agentNodeNames,
    { enabled: !isConversational && hasAgentAction && !!selectedRunId }
  );

  // Combine chat history from the appropriate source
  const chatHistory = isConversational || isChatMessageWorkflow
    ? runLevelChatHistory
    : actionLevelChatHistory;

  // For chatMessage workflows, determine the number of turns from chatHistory
  // We need this to proactively fetch all iteration actions for canvas badges
  const chatMessageTurnCount = useMemo(() => {
    if (!isChatMessageWorkflow || !chatHistory || chatHistory.length === 0) {
      return 0;
    }
    // Count unique taskSequenceIds (each represents a turn)
    const taskSequenceIds = new Set(
      chatHistory
        .map((msg) => msg.agentMetadata?.taskSequenceId)
        .filter(Boolean)
    );
    // If we have distinct taskSequenceIds, that's the turn count
    // Otherwise, fall back to max iteration + 1
    if (taskSequenceIds.size > 1) {
      return taskSequenceIds.size;
    }
    const maxIteration = Math.max(...chatHistory.map((msg) => msg.iteration ?? 0));
    return maxIteration + 1;
  }, [isChatMessageWorkflow, chatHistory]);

  // Effect to proactively fetch all chatMessage iteration actions when workflow is detected
  // This ensures canvas badges show execution status for OMT workflows
  useEffect(() => {
    if (!isChatMessageWorkflow || chatMessageTurnCount === 0) {
      return;
    }
    
    // If there's an active fetch, wait for it
    if (chatMessageIterationFetchRequest !== null) {
      return;
    }
    
    // Check if there are any pending items that need to be started
    // This handles the case where items were marked pending but the fetch never started
    const pendingItems = Array.from(pendingChatMessageIterationFetches);
    const notYetLoaded = pendingItems.filter(i => !chatMessageIterationActions.has(i));
    if (notYetLoaded.length > 0) {
      setChatMessageIterationFetchRequest(notYetLoaded[0]!);
      return;
    }
    
    // Queue up all iterations that haven't been fetched yet
    const iterationsToFetch: number[] = [];
    for (let i = 0; i < chatMessageTurnCount; i++) {
      if (!chatMessageIterationActions.has(i) && !pendingChatMessageIterationFetches.has(i)) {
        iterationsToFetch.push(i);
      }
    }
    if (iterationsToFetch.length === 0) {
      return;
    }
    const firstIteration = iterationsToFetch[0]!; // Safe: we just checked length > 0
    // Mark all as pending and start the first fetch
    setPendingChatMessageIterationFetches((prev) => {
      const next = new Set(prev);
      iterationsToFetch.forEach((i) => next.add(i));
      return next;
    });
    // Start fetching the first one
    setChatMessageIterationFetchRequest(firstIteration);
  }, [isChatMessageWorkflow, chatMessageTurnCount, chatMessageIterationActions, pendingChatMessageIterationFetches, chatMessageIterationFetchRequest]);

  // Build iteration info for built-in tools (code_interpreter, file_search)
  // This allows the hook to fetch input/output for built-in tools from iteration-level content links
  const toolActionIterationInfo = useMemo(() => {
    if (!toolActionFetchRequest || !agentIterationsData) return null;

    const iterations = agentIterationsData.get(toolActionFetchRequest.agentName);
    if (!iterations) return null;

    const iteration = iterations.find(i => i.name === toolActionFetchRequest.iterationName);
    if (!iteration) return null;

    // Build iteration info with conditional optional properties (exactOptionalPropertyTypes compliance)
    const info: BuiltInToolIterationInfo = {
      tools: iteration.tools.map(t => ({
        toolName: t.toolName,
        status: t.status,
      })),
    };
    if (iteration.inputsLink) {
      info.inputsLink = iteration.inputsLink;
    }
    if (iteration.outputsLink) {
      info.outputsLink = iteration.outputsLink;
    }
    if (iteration.startTime) {
      info.startTime = iteration.startTime;
    }
    if (iteration.endTime) {
      info.endTime = iteration.endTime;
    }
    return info;
  }, [toolActionFetchRequest, agentIterationsData]);

  // Fetch tool actions for the requested iteration (lazy-loaded when user clicks a tool)
  const { data: fetchedToolActions, isLoading: isLoadingToolActions } = useAgentIterationActions(
    selectedRunId,
    toolActionFetchRequest?.agentName ?? null,
    toolActionFetchRequest?.iterationName ?? null,
    toolActionIterationInfo,
    { enabled: !!toolActionFetchRequest }
  );

  // Effect to store fetched tool actions and process next in queue
  useEffect(() => {
    if (fetchedToolActions && toolActionFetchRequest && !isLoadingToolActions) {
      const iterationKey = `${toolActionFetchRequest.agentName}-${toolActionFetchRequest.iterationName}`;
      setToolActions(prev => {
        const next = new Map(prev);
        next.set(iterationKey, fetchedToolActions);
        return next;
      });
      setPendingToolActionFetches(prev => {
        const next = new Set(prev);
        next.delete(iterationKey);
        return next;
      });
      // Clear the current request
      setToolActionFetchRequest(null);
    }
  }, [fetchedToolActions, toolActionFetchRequest, isLoadingToolActions]);

  // Effect to process next item in the queue when current request is done
  useEffect(() => {
    if (!toolActionFetchRequest && toolActionFetchQueue.length > 0) {
      const nextRequest = toolActionFetchQueue[0];
      if (nextRequest) {
        const rest = toolActionFetchQueue.slice(1);
        setToolActionFetchQueue(rest);
        setToolActionFetchRequest(nextRequest);
      }
    }
  }, [toolActionFetchRequest, toolActionFetchQueue]);

  // Handler to fetch tool actions for an iteration (called by LogsPanel when tool is clicked or auto-fetched)
  const handleFetchToolActions = useCallback((agentName: string, iterationName: string) => {
    const iterationKey = `${agentName}-${iterationName}`;
    // Skip if already loaded or currently loading/queued
    if (toolActions.has(iterationKey) || pendingToolActionFetches.has(iterationKey)) {
      return;
    }
    // Mark as pending
    setPendingToolActionFetches(prev => {
      const next = new Set(prev);
      next.add(iterationKey);
      return next;
    });
    // Add to queue (will be processed by the effect)
    setToolActionFetchQueue(prev => [...prev, { agentName, iterationName }]);
  }, [toolActions, pendingToolActionFetches]);

  // Fetch scope repetitions for the current request
  const { data: fetchedScopeRepetitions, isLoading: isLoadingScopeRepetitions } = useAllRepetitions(
    selectedRunId,
    scopeRepetitionFetchRequest,
    { enabled: !!scopeRepetitionFetchRequest }
  );

  // Effect to store fetched scope repetitions and process next in queue
  useEffect(() => {
    if (fetchedScopeRepetitions && scopeRepetitionFetchRequest && !isLoadingScopeRepetitions) {
      setScopeRepetitions(prev => {
        const next = new Map(prev);
        next.set(scopeRepetitionFetchRequest, fetchedScopeRepetitions);
        return next;
      });
      setPendingScopeRepetitionFetches(prev => {
        const next = new Set(prev);
        next.delete(scopeRepetitionFetchRequest);
        // Pick next pending scope to fetch
        const nextPending = Array.from(next).find(name => !scopeRepetitions.has(name));
        if (nextPending) {
          setScopeRepetitionFetchRequest(nextPending);
        } else {
          setScopeRepetitionFetchRequest(null);
        }
        return next;
      });
    }
  }, [fetchedScopeRepetitions, scopeRepetitionFetchRequest, isLoadingScopeRepetitions, scopeRepetitions]);

  // In draft/streaming mode, populate scopeRepetitions directly from streaming iteration data
  // because no persisted run exists and the API fetch would fail.
  // Uses baseNodeExecutions (available before the enriched nodeExecutions memo).
  useEffect(() => {
    if (!isDraftMode) return;

    const scopeTypes = ['foreach', 'until', 'scope', 'if', 'switch'];
    const updates = new Map<string, RepetitionInfo[]>();

    for (const exec of baseNodeExecutions) {
      if (!exec.nodeType || !scopeTypes.includes(exec.nodeType.toLowerCase())) continue;
      if (!exec.iterations || exec.iterations.length === 0) continue;

      const reps: RepetitionInfo[] = exec.iterations.map((iter) => ({
        name: String(iter.index).padStart(6, '0'),
        index: iter.index,
        status: iter.status,
        ...(iter.startTime ? { startTime: iter.startTime } : {}),
        ...(iter.endTime ? { endTime: iter.endTime } : {}),
        ...(iter.duration !== undefined ? { duration: iter.duration } : {}),
      }));
      updates.set(exec.nodeName, reps);
    }

    if (updates.size > 0) {
      setScopeRepetitions(prev => {
        const next = new Map(prev);
        for (const [key, val] of updates) {
          next.set(key, val);
        }
        return next;
      });
    }
  }, [isDraftMode, baseNodeExecutions]);

  // Handler to fetch scope repetitions (called by LogsPanel when scope is expanded)
  const handleFetchScopeRepetitions = useCallback((scopeName: string) => {
    // In draft mode, data is pre-populated from streaming — no API fetch needed
    if (isDraftMode && scopeRepetitions.has(scopeName)) {
      return;
    }
    // Skip if already loaded or currently loading
    if (scopeRepetitions.has(scopeName) || pendingScopeRepetitionFetches.has(scopeName)) {
      return;
    }
    // Mark as pending
    setPendingScopeRepetitionFetches(prev => {
      const next = new Set(prev);
      next.add(scopeName);
      return next;
    });
    // Set as current request or queue for later
    setScopeRepetitionFetchRequest(prev => prev ?? scopeName);
  }, [isDraftMode, scopeRepetitions, pendingScopeRepetitionFetches]);

  // Handler when a repetition is selected in LogsPanel or the side-panel
  // IterationSelector. Updates BOTH the selected repetition (drives detail
  // content + LogsPanel highlight) AND the canvas iteration index (drives
  // the FloatingForeachNode '< N/M >' pager and any inner-action overlays).
  const handleRepetitionSelect = useCallback((scopeName: string, repetitionName: string) => {
    setSelectedScopeRepetitions(prev => ({
      ...prev,
      [scopeName]: repetitionName,
    }));
    const reps = scopeRepetitionsRef.current.get(scopeName);
    const idx = reps?.findIndex(r => r.name === repetitionName) ?? -1;
    if (idx >= 0) {
      setScopeIterations(curr => ({
        ...curr,
        [scopeName]: idx,
      }));
    }
  }, []);

  // === Scope Actions (forEach/Until loops) ===
  // Derive child action names for the requested scope from baseNodeExecutions.
  // Otto exposes `/actions/{childName}/repetitions/{rep}` per child rather than
  // a per-iteration scope endpoint, so we need to know who the children are.
  const scopeActionChildNames = useMemo(() => {
    if (!scopeActionFetchRequest) return [];
    const { scopeName } = scopeActionFetchRequest;
    return baseNodeExecutions
      .filter((e) => e.parentNodeId === scopeName)
      .map((e) => e.nodeName);
  }, [scopeActionFetchRequest, baseNodeExecutions]);

  // Fetch scope actions (actions inside a repetition) for the current request
  const { data: fetchedScopeActions, isLoading: isLoadingScopeActions } = useRepetitionScopeActions(
    selectedRunId,
    scopeActionFetchRequest?.scopeName ?? null,
    scopeActionFetchRequest?.repetitionName ?? null,
    scopeActionChildNames
  );

  // Effect to store fetched scope actions and process next in queue
  useEffect(() => {
    if (fetchedScopeActions && scopeActionFetchRequest && !isLoadingScopeActions) {
      const actionKey = `${scopeActionFetchRequest.scopeName}-${scopeActionFetchRequest.repetitionName}`;
      setScopeActions(prev => {
        const next = new Map(prev);
        next.set(actionKey, fetchedScopeActions);
        return next;
      });
      pendingScopeActionFetchesRef.current.delete(actionKey);
      setPendingScopeActionFetches(prev => {
        const next = new Set(prev);
        next.delete(actionKey);
        return next;
      });
      // Clear the current request
      setScopeActionFetchRequest(null);
    }
  }, [fetchedScopeActions, scopeActionFetchRequest, isLoadingScopeActions]);

  // Effect to process next item in the scope action fetch queue
  useEffect(() => {
    if (!scopeActionFetchRequest && scopeActionFetchQueue.length > 0) {
      const nextRequest = scopeActionFetchQueue[0];
      if (nextRequest) {
        const rest = scopeActionFetchQueue.slice(1);
        setScopeActionFetchQueue(rest);
        setScopeActionFetchRequest(nextRequest);
      }
    }
  }, [scopeActionFetchRequest, scopeActionFetchQueue]);

  // Handler to fetch scope actions for a repetition (called by LogsPanel when scope is expanded)
  const handleFetchScopeActions = useCallback((scopeName: string, repetitionName: string) => {
    const actionKey = `${scopeName}-${repetitionName}`;
    // Skip if already loaded.
    if (scopeActions.has(actionKey)) {
      return;
    }
    // Synchronous dedupe via ref. `pendingScopeActionFetches` (state) is stale
    // within rapid double calls because React hasn't re-rendered yet; the ref
    // is mutated immediately, so StrictMode's double effect invocation (or any
    // other near-simultaneous duplicate call) cannot enqueue two fetches for
    // the same key.
    if (pendingScopeActionFetchesRef.current.has(actionKey)) {
      return;
    }
    pendingScopeActionFetchesRef.current.add(actionKey);
    setPendingScopeActionFetches((prev) => {
      const next = new Set(prev);
      next.add(actionKey);
      return next;
    });
    // Add to queue (will be processed by the effect)
    setScopeActionFetchQueue((prev) => [...prev, { scopeName, repetitionName }]);
  }, [scopeActions]);

  // Auto-fetch per-iteration child actions whenever the user selects an
  // iteration (via canvas pager, side-panel IterationSelector, or LogsPanel
  // iteration row). All three paths funnel through `selectedScopeRepetitions`,
  // so we watch it here. `handleFetchScopeActions` already deduplicates against
  // `scopeActions` and `pendingScopeActionFetches`, so revisiting a cached
  // iteration is a no-op.
  useEffect(() => {
    for (const [scopeName, repetitionName] of Object.entries(selectedScopeRepetitions)) {
      if (!repetitionName) continue;
      handleFetchScopeActions(scopeName, repetitionName);
    }
  }, [selectedScopeRepetitions, handleFetchScopeActions]);

  // === ChatMessage Iteration Actions (OMT workflows) ===
  // Fetch chatMessage iteration actions as NodeExecution[] - same type as stateful!
  // NOTE: Non-OMT Agent workflows (kind: Agent with A2A) use useAllAgentIterations instead.
  const { data: fetchedChatMessageActions, isLoading: isLoadingChatMessageActions } = useChatMessageIterationNodeExecutions(
    selectedRunId,
    chatMessageIterationFetchRequest,
    { enabled: chatMessageIterationFetchRequest !== null }
  );

  // Effect to store fetched chatMessage iteration actions and process next in queue
  useEffect(() => {
    if (fetchedChatMessageActions && chatMessageIterationFetchRequest !== null && !isLoadingChatMessageActions) {
      setChatMessageIterationActions(prev => {
        const next = new Map(prev);
        next.set(chatMessageIterationFetchRequest, fetchedChatMessageActions);
        return next;
      });
      setPendingChatMessageIterationFetches(prev => {
        const next = new Set(prev);
        next.delete(chatMessageIterationFetchRequest);
        // Process next pending item if any
        const nextItem = Array.from(next)[0];
        if (nextItem !== undefined) {
          setTimeout(() => setChatMessageIterationFetchRequest(nextItem), 0);
        } else {
          setChatMessageIterationFetchRequest(null);
        }
        return next;
      });
    }
  }, [fetchedChatMessageActions, chatMessageIterationFetchRequest, isLoadingChatMessageActions]);

  // Handler to fetch chatMessage iteration actions (called by LogsPanel for each turn)
  const handleFetchChatMessageIterationActions = useCallback((iterationIndex: number) => {
    // Skip if already loaded or currently loading
    if (chatMessageIterationActions.has(iterationIndex) || pendingChatMessageIterationFetches.has(iterationIndex)) {
      return;
    }
    // Mark as pending
    setPendingChatMessageIterationFetches(prev => {
      const next = new Set(prev);
      next.add(iterationIndex);
      return next;
    });
    // Set as current request (simple approach - one at a time)
    if (chatMessageIterationFetchRequest === null) {
      setChatMessageIterationFetchRequest(iterationIndex);
    }
  }, [chatMessageIterationActions, pendingChatMessageIterationFetches, chatMessageIterationFetchRequest]);

  // Create a Set of agent names that are loading (for LogsPanel)
  const loadingAgentIterations = useMemo(() => {
    return isAgentIterationsLoading ? new Set(agentNodeNames) : new Set<string>();
  }, [isAgentIterationsLoading, agentNodeNames]);

  // Get node executions to display, enriched with iteration count from fetched data
  // For kind: Agent workflows, agent actions are filtered from run API, so we synthesize them from timeline data
  // For chatMessage workflows (OMT), we show the LATEST iteration's status on the canvas
  const nodeExecutions = useMemo((): NodeExecution[] => {
    let result = baseNodeExecutions;

    // === Handle chatMessage workflows (OMT) ===
    // For chatMessage workflows, the action nodes are executed per-turn.
    // Show the LATEST iteration's status on the canvas (not merged/best status).
    // This gives users an accurate view of the current state of the workflow.
    if (isChatMessageWorkflow && chatMessageIterationActions.size > 0) {
      // Find the highest iteration index (latest turn)
      const latestIterationIndex = Math.max(...Array.from(chatMessageIterationActions.keys()));
      const latestIterationActions = chatMessageIterationActions.get(latestIterationIndex) ?? [];

      // Convert to NodeExecution[] with proper nodeId format for canvas matching
      const chatMessageExecutions: NodeExecution[] = latestIterationActions.map(exec => ({
        ...exec,
        // Ensure nodeId matches canvas node format (action-{name})
        nodeId: `action-${exec.nodeName}`,
      }));

      // Build a set of action names from iteration data for quick lookup
      const iterationActionNames = new Set(chatMessageExecutions.map(e => e.nodeName));

      // Keep non-action nodes from base (trigger, orchestrator) 
      // AND action nodes that are NOT in the iteration data (they may not have executed yet)
      // Replace action nodes that ARE in iteration data with the latest iteration's status
      const filteredBase = result.filter(e => {
        const isActionNode = e.nodeId.startsWith('action-');
        if (!isActionNode) return true; // Keep trigger/orchestrator
        // For actions: keep ONLY if NOT in iteration data (iteration data will replace)
        return !iterationActionNames.has(e.nodeName);
      });

      result = [...filteredBase, ...chatMessageExecutions];
    }

    // === Handle kind: Agent workflows and Stateful workflows with Agent actions ===
    if (agentIterationsData && agentIterationsData.size > 0) {
      // Enrich existing agent nodes with iteration count from fetched data
      result = result.map((exec) => {
        const iterations = agentIterationsData.get(exec.nodeName);
        if (iterations && iterations.length > 0) {
          return {
            ...exec,
            iterationCount: iterations.length,
            // Also attach the iterations data for LogsPanel
            iterations,
          };
        }
        return exec;
      });

      // For kind: Agent workflows, the agent action is filtered from run API response
      // Synthesize agent node executions from timeline data for agents not already in result
      const existingNodeNames = new Set(result.map(exec => exec.nodeName));
      const synthesizedAgentNodes: NodeExecution[] = [];

      for (const [agentName, iterations] of agentIterationsData.entries()) {
        if (!existingNodeNames.has(agentName) && iterations.length > 0) {
          // Get timing info from the iterations
          const firstIteration = iterations[0];
          const lastIteration = iterations[iterations.length - 1];
          const startTime = firstIteration?.startTime;
          const endTime = lastIteration?.endTime;

          // Determine status from the last iteration
          let status: 'running' | 'succeeded' | 'failed' | 'pending' = 'running';
          if (lastIteration?.status === 'succeeded') {
            status = 'succeeded';
          } else if (lastIteration?.status === 'failed') {
            status = 'failed';
          } else if (lastIteration?.status === 'running') {
            status = 'running';
          }

          synthesizedAgentNodes.push({
            nodeId: `action-${agentName}`,
            nodeName: agentName,
            nodeType: 'Agent',
            status,
            startTime,
            endTime,
            iterationCount: iterations.length,
            iterations,
          });
        }
      }

      result = [...result, ...synthesizedAgentNodes];
    }

    // === Handle ForEach scope iteration selection ===
    // For each scope where the user has selected a specific iteration (via canvas
    // pager / iteration scrubber / log row), override the child action statuses
    // and durations on the canvas with that iteration's data so badges update
    // when paging through iterations.
    if (selectedScopeRepetitions && Object.keys(selectedScopeRepetitions).length > 0 && scopeActions.size > 0) {
      const iterationOverrides = new Map<string, NodeExecution>();
      for (const [scopeName, repetitionName] of Object.entries(selectedScopeRepetitions)) {
        const actionKey = `${scopeName}-${repetitionName}`;
        const iterationActions = scopeActions.get(actionKey);
        if (!iterationActions) continue;
        for (const childExec of iterationActions) {
          // Index by both bare action name and scoped name so the canvas
          // executionLookup (which uses unscoped fallbacks) finds the override.
          iterationOverrides.set(childExec.nodeName, childExec);
        }
      }
      if (iterationOverrides.size > 0) {
        result = result.map((exec) => {
          const override = iterationOverrides.get(exec.nodeName);
          if (!override) return exec;
          // Only override fields that are defined on the iteration result, so a
          // running iteration (no endTime/duration yet) doesn't blow away the
          // existing aggregate values.
          const merged: NodeExecution = { ...exec, status: override.status };
          if (override.duration !== undefined) merged.duration = override.duration;
          if (override.startTime !== undefined) merged.startTime = override.startTime;
          if (override.endTime !== undefined) merged.endTime = override.endTime;
          return merged;
        });
      }
    }

    return result;
  }, [baseNodeExecutions, agentIterationsData, isChatMessageWorkflow, chatMessageIterationActions, selectedScopeRepetitions, scopeActions]);

  // Get selected node execution for logs panel, enriched with action details
  const selectedNodeExecution = useMemo((): NodeExecution | null => {
    if (!selectedNodeId) return null;

    // For chatMessage actions, look up status from chatMessageIterationActions
    // ChatMessage action format: "chatmsg-action-{iterationIndex}-{actionName}"
    if (selectedNodeId.startsWith(NODE_ID_PREFIX_CHATMSG_ACTION)) {
      const withoutPrefix = selectedNodeId.slice(NODE_ID_PREFIX_CHATMSG_ACTION.length);
      const firstDashIndex = withoutPrefix.indexOf('-');
      const iterationIndexStr = firstDashIndex >= 0 ? withoutPrefix.slice(0, firstDashIndex) : '';
      const actionName = firstDashIndex >= 0 ? withoutPrefix.slice(firstDashIndex + 1) : withoutPrefix;
      const iterationIndex = parseInt(iterationIndexStr, 10);

      // Look up the actual node execution from chatMessageIterationActions
      let status: NodeExecution['status'] = 'succeeded';
      if (!isNaN(iterationIndex) && chatMessageIterationActions) {
        const iterationActions = chatMessageIterationActions.get(iterationIndex);
        const matchingAction = iterationActions?.find(a => a.nodeName === actionName);
        if (matchingAction) {
          status = matchingAction.status;
        }
      }

      // Return NodeExecution with status from iteration data and input/output from actionDetails
      return {
        nodeId: selectedNodeId,
        nodeName: actionName,
        nodeType: 'Action',
        status,
        input: actionDetails?.input,
        output: actionDetails?.output,
      };
    }

    // For tool nodes, look up data from toolActions
    // Tool node format: "tool-{agentName}-{toolName}"
    if (selectedNodeId.startsWith('tool-')) {
      const withoutPrefix = selectedNodeId.slice(5); // Remove "tool-"
      const firstDashIndex = withoutPrefix.indexOf('-');
      if (firstDashIndex >= 0) {
        const agentName = withoutPrefix.slice(0, firstDashIndex);
        const toolName = withoutPrefix.slice(firstDashIndex + 1);

        // Find the tool action data from toolActions
        // toolActions is keyed by "{agentName}-{iterationName}" (e.g., "Agent_c51e-000000")
        // If we have a selected iteration, prefer looking up that specific iteration
        const iterationName = selectedToolIteration !== null
          ? String(selectedToolIteration).padStart(6, '0') // Format as "000000", "000001", etc.
          : null;
        const preferredKey = iterationName ? `${agentName}-${iterationName}` : null;

        // First try the preferred iteration, then fall back to searching all iterations
        const keysToSearch = preferredKey
          ? [preferredKey, ...Array.from(toolActions.keys()).filter(k => k !== preferredKey && k.startsWith(agentName + '-'))]
          : Array.from(toolActions.keys()).filter(k => k.startsWith(agentName + '-'));

        for (const iterationKey of keysToSearch) {
          const actions = toolActions.get(iterationKey);
          if (actions) {
            const matchingAction = actions.find(a =>
              a.toolName === toolName || a.actionName === toolName
            );
            if (matchingAction) {
              return {
                nodeId: selectedNodeId,
                nodeName: matchingAction.actionName || toolName,
                nodeType: 'Tool',
                status: matchingAction.status,
                input: matchingAction.input,
                output: matchingAction.output,
                startTime: matchingAction.startTime,
                endTime: matchingAction.endTime,
                duration: matchingAction.duration,
                error: matchingAction.error,
              };
            }
          }
        }

        // If not found in toolActions, return a basic node execution
        // This can happen for built-in tools like code_interpreter when iteration hasn't been expanded yet
        return {
          nodeId: selectedNodeId,
          nodeName: toolName,
          nodeType: 'Tool',
          status: 'succeeded',
        };
      }
    }

    if (nodeExecutions.length === 0 && scopeActions.size === 0) return null;

    // Build a name-based index for O(1) fallback lookups (avoids repeated linear scans)
    const nodeNameIndex = new Map<string, NodeExecution>();
    for (const exec of nodeExecutions) {
      if (!nodeNameIndex.has(exec.nodeName)) {
        nodeNameIndex.set(exec.nodeName, exec);
      }
    }

    const nodeExec = nodeExecutions.find((n: NodeExecution) => n.nodeId === selectedNodeId);

    // If not found by exact ID, try actionName fallback for scoped branch nodes
    // (e.g., canvas emits "action-IsApproved/Compose_1" but execution has "action-Compose_1")
    if (!nodeExec && selectedNodeId) {
      const actionName = selectedNodeId.replace(/^(trigger-|action-)/, '').split('/').pop();
      if (actionName) {
        const fallback = nodeNameIndex.get(actionName);
        if (fallback) return fallback;
      }
    }

    // If not found in top-level executions, check scope actions (child actions inside ForEach/Until repetitions)
    if (!nodeExec) {
      // Search the currently selected repetition first, then fall back to all scope actions
      for (const [scopeName, repetitionName] of Object.entries(selectedScopeRepetitions)) {
        const actionKey = `${scopeName}-${repetitionName}`;
        const actions = scopeActions.get(actionKey);
        const match = actions?.find(a => a.nodeId === selectedNodeId);
        if (match) return match;
      }
      // Fall back: search all scope actions
      for (const actions of scopeActions.values()) {
        const match = actions.find(a => a.nodeId === selectedNodeId);
        if (match) return match;
      }
      return null;
    }

    // Enrich with action details if available
    if (actionDetails) {
      return {
        ...nodeExec,
        input: actionDetails.input ?? nodeExec.input,
        output: actionDetails.output ?? nodeExec.output,
        agentMetadata: actionDetails.agentMetadata ?? nodeExec.agentMetadata,
        retryHistory: actionDetails.retryHistory ?? nodeExec.retryHistory,
      };
    }

    return nodeExec;
  }, [nodeExecutions, selectedNodeId, actionDetails, chatMessageIterationActions, toolActions, selectedToolIteration, scopeActions, selectedScopeRepetitions]);

  // Build Foundry portal URL for the selected agent action
  const foundryPortalUrl = useMemo((): string | undefined => {
    if (!selectedNodeExecution?.agentMetadata?.modelType) return undefined;
    if (selectedNodeExecution.agentMetadata.modelType !== 'FoundryAgentService' && selectedNodeExecution.agentMetadata.modelType !== 'FoundryAgentServiceV2') return undefined;

    // Get action definition from the run's workflow snapshot
    const definition = selectedRun?.workflowSnapshot?.definition as Record<string, unknown> | undefined;
    const actions = definition?.actions as Record<string, Record<string, unknown>> | undefined;
    const actionDef = actions?.[selectedNodeExecution.nodeName];
    const inputs = actionDef?.inputs as
      | { foundryAgentId?: string; foundryAgentVersion?: string; modelConfigurations?: { model1?: { referenceName?: string } } }
      | undefined;
    if (!inputs) return undefined;

    const refKey = inputs.modelConfigurations?.model1?.referenceName;
    if (!refKey) return undefined;

    const connRef = connectionReferences[refKey];
    const resourceId = connRef?.connectionProperties?.resourceId as string | undefined;
    const endpoint = connRef?.connectionProperties?.endpoint as string | undefined;
    const agentVersion = inputs.foundryAgentVersion === 'v1' ? 'v1' as const : 'v2' as const;
    const agentName = inputs.foundryAgentId;

    return buildFoundryPortalUrl(resourceId, endpoint, agentName, agentVersion, account?.tenantId, 'traces');
  }, [selectedNodeExecution, selectedRun, connectionReferences, account]);

  // Extract trigger name for resubmit.
  // The runtime's resubmit endpoint validates the trigger name against the CURRENT
  // workflow definition. The run's trigger.name may differ if the workflow was redeployed.
  // Priority: current definition (workflowStore) > run's workflow snapshot > run's trigger name.
  const triggerName = useMemo((): string | null => {
    // 1. Use the current definition's trigger key from workflowStore (fetched from VFS)
    const definitionTriggers = workflow?.definition?.triggers;
    if (definitionTriggers) {
      const keys = Object.keys(definitionTriggers);
      if (keys.length > 0) {
        return keys[0]!;
      }
    }
    // 2. Use the run's workflow snapshot (from $expand=workflow/properties)
    const snapshotTriggers = selectedRun?.workflowSnapshot?.definition?.triggers;
    if (snapshotTriggers && typeof snapshotTriggers === 'object') {
      const keys = Object.keys(snapshotTriggers);
      if (keys.length > 0) {
        return keys[0]!;
      }
    }
    // 3. Fallback: use trigger name from the run's execution data
    const triggerNode = nodeExecutions.find((n: NodeExecution) => n.nodeId.startsWith('trigger-'));
    if (!triggerNode) return null;
    return triggerNode.nodeName;
  }, [workflow?.definition?.triggers, selectedRun?.workflowSnapshot, nodeExecutions]);

  const canRerunSelectedRun = useMemo((): boolean => {
    if (!selectedRun || selectedRun.status === 'running' || !triggerName || !hasEditPermission) return false;
    if (selectedRun.isDraftRun) return false;

    const triggerTypeFromWorkflow = workflow?.definition?.triggers?.[triggerName]?.type;
    const snapshotDefinition = selectedRun.workflowSnapshot?.definition as
      | { triggers?: Record<string, { type?: string }> }
      | undefined;
    const triggerTypeFromSnapshot = snapshotDefinition?.triggers?.[triggerName]?.type;

    return canTriggerRunPublishedWorkflow(triggerTypeFromWorkflow ?? triggerTypeFromSnapshot);
  }, [
    selectedRun,
    triggerName,
    hasEditPermission,
    workflow?.definition?.triggers,
  ]);

  // Handler for resubmitting a run
  const handleResubmitRun = useCallback(async () => {
    if (!selectedRun || !triggerName) return;
    await resubmitMutation.mutateAsync({
      runId: selectedRun.id,
      triggerName,
    });
  }, [selectedRun, triggerName, resubmitMutation]);

  // Handler for canceling a run
  const handleCancelRun = useCallback(async () => {
    if (!selectedRun) return;
    await cancelMutation.mutateAsync({
      runId: selectedRun.id,
    });
  }, [selectedRun, cancelMutation]);

  // Handler for exporting run data as JSON
  const handleExportRun = useCallback(() => {
    if (!selectedRun) return;

    // Create export data structure
    const exportData = {
      exportedAt: new Date().toISOString(),
      run: {
        id: selectedRun.id,
        workflowName: selectedRun.workflowName,
        status: selectedRun.status,
        startTime: selectedRun.startTime,
        endTime: selectedRun.endTime,
        duration: selectedRun.duration,
        isAgentic: selectedRun.isAgentic,
        executionCount: selectedRun.executionCount,
      },
      executions: selectedRun.executions?.map((exec: Execution) => ({
        id: exec.id,
        status: exec.status,
        timestamp: exec.timestamp,
        duration: exec.duration,
        nodeExecutions: exec.nodeExecutions.map((node: NodeExecution) => ({
          nodeId: node.nodeId,
          nodeName: node.nodeName,
          nodeType: node.nodeType,
          status: node.status,
          startTime: node.startTime,
          endTime: node.endTime,
          duration: node.duration,
          error: node.error,
          input: node.input,
          output: node.output,
        })),
      })),
    };

    // Create and download JSON file
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `run-${selectedRun.id}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [selectedRun]);

  // Handler for generating a recipe template from a succeeded run
  const handleGenerateTemplate = useCallback(async () => {
    if (!selectedRun || !projectName || !appName || !workflowName || !triggerName) return;

    setIsGeneratingRecipe(true);
    try {
      const token = await getOttoToken();
      if (!token) {
        addNotification({
          type: 'error',
          title: 'Template Generation Failed',
          message: 'Authentication token unavailable.',
        });
        return;
      }

      // Resolve workflow definition — prefer current from store, fall back to run snapshot.
      // The snapshot has a looser type (Record<string, unknown>) but the same runtime shape;
      // validateRecipe() in generateRecipeFromRun will catch any structural issues.
      const workflowDefinition: WorkflowDefinition | null = workflow
        ?? (selectedRun.workflowSnapshot
          ? (selectedRun.workflowSnapshot as unknown as WorkflowDefinition)
          : null);

      if (!workflowDefinition?.definition) {
        addNotification({
          type: 'error',
          title: 'Template Generation Failed',
          message: 'No workflow definition available for this run.',
        });
        return;
      }

      // Fetch run output links for the selected run.
      const runWithOutputLinksResult = await fetchRunWithAgentToolsWithOutputLinks(
        { projectName, appName, workflowName, accessToken: token },
        selectedRun.id,
      );

      if (!runWithOutputLinksResult) {
        addNotification({
          type: 'error',
          title: 'Template Generation Failed',
          message: 'Failed to fetch run output data.',
        });
        return;
      }

      // Determine trigger type from the definition
      const triggerType = workflowDefinition.definition.triggers[triggerName]?.type ?? 'Request';

      // Build fetchContent adapter
      const fetchContentAdapter = (uri: string) =>
        fetchActionContent({ uri }, token);

      const recipe = await generateRecipeFromRun({
        runId: selectedRun.id,
        workflowName,
        workflowDefinition,
        triggerName,
        triggerType,
        runWithOutputLinks: runWithOutputLinksResult,
        fetchContent: fetchContentAdapter,
        connections,
        // Notes track the active branch: draft sticky-notes in draft mode,
        // published in read-only view. Fall back to prod when the draft
        // sidecar is empty (notes weren't edited in draft).
        notes: (() => {
          const { prodNotes, draftNotes } = useDraftModeStore.getState();
          const picked = selectedRun.isDraftRun ? draftNotes : prodNotes;
          return picked as WorkflowDefinition['notes'] | null;
        })(),
      });

      // Hand off to the create-recipe dialog (metadata → sanitize → download)
      // instead of downloading immediately.
      setGeneratedRecipe(recipe);
    } catch (error) {
      console.error('[Generate Template] Failed:', error);
      addNotification({
        type: 'error',
        title: 'Template Generation Failed',
        message: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsGeneratingRecipe(false);
    }
  }, [selectedRun, projectName, appName, workflowName, triggerName, getOttoToken, workflow, connections, addNotification]);

  // Refresh: manual revalidation of all queries scoped to the current run.
  // Auto-polling stops once a run reaches a terminal state; this gives users
  // an explicit way to re-fetch on demand and reassurance for in-progress runs.
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshAnnouncement, setRefreshAnnouncement] = useState('');
  // Counter ensures consecutive same-outcome refreshes produce a unique
  // string, so aria-live re-announces (most SRs skip identical text nodes).
  const refreshAnnouncementSeqRef = useRef(0);
  const announceRefresh = useCallback((message: string) => {
    refreshAnnouncementSeqRef.current += 1;
    // Append zero-width spaces — invisible and silent, but make the text node
    // distinct from the previous announcement so SRs treat it as a new update.
    // Alternates between 1 and 2 zero-width spaces per call.
    const zeroWidthSpaces = '\u200B'.repeat(1 + (refreshAnnouncementSeqRef.current % 2));
    setRefreshAnnouncement(`${message}${zeroWidthSpaces}`);
  }, []);
  const handleRefreshRun = useCallback(async () => {
    if (!selectedRunId || isRefreshing) return;
    setIsRefreshing(true);
    setRefreshAnnouncement('');
    const startedAt = performance.now();
    try {
      // Explicit allow-list of query-key prefixes that are run-scoped (runId at
      // index 1). Avoids false positives from `queryKey.includes(runId)` which
      // would match any key that happens to contain the same string anywhere.
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key) || key[1] !== selectedRunId) return false;
          const prefix = key[0];
          return RUN_SCOPED_QUERY_KEY_PREFIXES.has(typeof prefix === 'string' ? prefix : '');
        },
      });
      announceRefresh('Run data refreshed');
      telemetryService.trackEvent(
        'monitoring.run.refresh',
        { run_status: selectedRun?.status ?? 'unknown' },
        { duration_ms: performance.now() - startedAt },
      );
    } catch (error) {
      announceRefresh('Refresh failed');
      telemetryService.trackException(error instanceof Error ? error : new Error(String(error)), {
        context: 'monitoring.run.refresh',
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient, selectedRunId, selectedRun?.status, isRefreshing, announceRefresh]);

  // Ticking relative-time label for the Refresh button ("Updated 12s ago").
  // Self-scheduling timeout so the cadence (1s while fresh, 1m otherwise) is
  // recomputed from `runDataUpdatedAt` only — without rescheduling on every tick.
  const [refreshNow, setRefreshNow] = useState(() => Date.now());
  useEffect(() => {
    if (!runDataUpdatedAt) return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (cancelled) return;
      const ageSeconds = (Date.now() - runDataUpdatedAt) / 1000;
      const delay = ageSeconds < 60 ? 1000 : 60000;
      timeoutId = setTimeout(() => {
        setRefreshNow(Date.now());
        schedule();
      }, delay);
    };
    schedule();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [runDataUpdatedAt]);

  const refreshRelativeLabel = useMemo(() => {
    if (!runDataUpdatedAt) return '';
    const seconds = Math.max(0, Math.floor((refreshNow - runDataUpdatedAt) / 1000));
    if (seconds < 5) return 'Updated just now';
    if (seconds < 60) return `Updated ${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `Updated ${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Updated ${hours}h ago`;
    return `Updated ${canonicalFormatDateTime(runDataUpdatedAt, 'locale', { fallback: '' })}`;
  }, [runDataUpdatedAt, refreshNow]);

  // Check if current run can be loaded into editor
  const canDebugInEditor = useMemo(() => {
    if (!selectedRun) return false;
    return canLoadIntoEditor(selectedRun);
  }, [selectedRun]);

  // Select a run — update state and URL
  const navigateToRun = useCallback((runId: string) => {
    setSelectedRunId(runId);
    setSelectedExecutionId(null);
    setSelectedNodeId(null);
    if (workflowName) {
      navigate(`${workflowBasePath}/runs/${encodeURIComponent(runId)}`, { replace: true });
    }
  }, [workflowName, workflowBasePath, navigate]);

  const handleSelectRun = (runId: string) => {
    // Reset agent/scope/tool state when switching runs
    setAgentIterations({});
    setScopeIterations({});
    setToolActions(new Map());
    setPendingToolActionFetches(new Set());
    setToolActionFetchQueue([]);
    setToolActionFetchRequest(null);
    setScopeRepetitions(new Map());
    setPendingScopeRepetitionFetches(new Set());
    setSelectedScopeRepetitions({});
    setScopeRepetitionFetchRequest(null);

    navigateToRun(runId);
  };

  const handleSelectExecution = (executionId: string) => {
    setSelectedExecutionId(executionId);
    // Reset agent iteration pager state when switching executions
    setAgentIterations({});
    // Reset scope iteration pager state when switching executions
    setScopeIterations({});
    // Reset tool actions state when switching executions
    setToolActions(new Map());
    setPendingToolActionFetches(new Set());
    setToolActionFetchRequest(null);
    // Reset scope repetition state when switching executions
    setScopeRepetitions(new Map());
    setPendingScopeRepetitionFetches(new Set());
    setSelectedScopeRepetitions({});
    setScopeRepetitionFetchRequest(null);

    // Don't auto-select any node - let user click to select
    setSelectedNodeId(null);
  };

  const handleNodeClick = (nodeId: string, iterationIndex?: number, _toolId?: string) => {
    // Set the selected node ID
    // When an action is selected in LogsPanel, the nodeId will be the action's tool node ID format
    setSelectedNodeId(nodeId);
    // Track the iteration index for tool nodes (used for looking up tool action data)
    setSelectedToolIteration(iterationIndex ?? null);
  };

  const handleTabChange = useCallback((tab: 'designer' | 'monitoring') => {
    if (!workflowName) return;
    if (tab === 'designer') {
      clearExecution();
      navigate(workflowBasePath, { replace: false });
    } else {
      setCodePanelState('minimized');
      // Don't reset assistant/chat state here — the assistant panel only
      // renders inside renderDesignerContent, so it's already hidden in
      // monitoring. Mutating these flags only breaks the rail on return
      // (designer defaults to isAssistantMinimized=true so the Copilot rail
      // should still be visible after a monitoring round-trip).

      if (selectedRunId) {
        // Respect the user's persisted runListOpen preference here — don't
        // force the list closed just because a run is selected.
        navigate(`${workflowBasePath}/runs/${encodeURIComponent(selectedRunId)}`, { replace: false });
      } else if (unfilteredRuns?.length) {
        const latestRun = unfilteredRuns.reduce((latest, run) =>
          !latest || new Date(run.startTime).getTime() > new Date(latest.startTime).getTime() ? run : latest
        );
        setSelectedRunId(latestRun.id);
        navigate(`${workflowBasePath}/runs/${encodeURIComponent(latestRun.id)}`, { replace: false });
      } else {
        // The empty-state header keeps the run-list toggle visible, so honor
        // the user's runListOpen preference instead of forcing the list open
        // here.
        navigate(`${workflowBasePath}/runs`, { replace: false });
      }
    }
  }, [clearExecution, navigate, workflowName, workflowBasePath, selectedRunId, unfilteredRuns]);

  // Tab-switching shortcuts: ⌘⇧1 / Ctrl+Shift+1 (Designer),
  // ⌘⇧2 / Ctrl+Shift+2 (Monitoring). Routed through the registry so
  // they fire on the platform's primary modifier (the previous inline
  // `e.ctrlKey` check silently broke this on Mac).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (shouldSkipShortcut(e)) return;

      if (matchesShortcut(e, 'switchToDesigner')) {
        e.preventDefault();
        if (e.repeat) return;
        handleTabChange('designer');
      } else if (matchesShortcut(e, 'switchToMonitoring')) {
        e.preventDefault();
        if (e.repeat) return;
        handleTabChange('monitoring');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleTabChange]);

  // Handle clicking the "Debug in Editor" button from runs view
  const handleDebugInEditor = useCallback(() => {
    if (!selectedRun || !workflowName) return;
    const execution = selectedExecution ?? selectedRun.executions?.[0];
    if (!execution) return;

    // Use enriched nodeExecutions (which includes iteration data from useAllAgentIterations)
    // instead of the raw execution.nodeExecutions
    const enrichedExecution: Execution = {
      ...execution,
      nodeExecutions: nodeExecutions.length > 0 ? nodeExecutions : execution.nodeExecutions,
    };

    const editorRunState = transformExecutionToEditorState(selectedRun, enrichedExecution, {
      autoPinTrigger: true,
      originalWorkflow: workflow,
    });
    loadExecution(editorRunState);

    // Update URL with debug run parameter
    navigate(`${workflowBasePath}?debugRun=${encodeURIComponent(selectedRun.id)}`, { replace: true });

    handleTabChange('designer');
  }, [selectedRun, selectedExecution, workflow, workflowName, workflowBasePath, loadExecution, navigate, nodeExecutions, handleTabChange]);


  // Handler to open the trigger workflow dialog (Run button on toolbar)
  const handleRunClick = useCallback(() => {
    setIsTriggerDialogOpen(true);
  }, []);

  // Workflow-level palette actions surfaced at the top of ⌘K. These are
  // intent-shaped shortcuts ("run this workflow", "create a new one in
  // this app") rather than canvas commands. The shortcut strings here are
  // visual hints — the actual hotkeys are bound elsewhere (or not yet).
  const paletteActions = useMemo((): PaletteAction[] => {
    const actions: PaletteAction[] = [];
    const displayWorkflowName = workflowName ? extractWorkflowName(workflowName) : '';
    if (displayWorkflowName) {
      actions.push({
        id: 'run-workflow-now',
        label: `Run "${displayWorkflowName}" now`,
        icon: <FlashRegular fontSize={14} />,
        execute: () => {
          handleRunClick();
        },
      });
    }
    if (locationName && projectName && appName) {
      actions.push({
        id: 'new-workflow-in-app',
        label: `New workflow in ${appName}`,
        icon: <AddRegular fontSize={14} />,
        execute: () => {
          // Open the in-place create-workflow flow (CreateWorkflowDialogHost
          // is mounted on the designer route) rather than routing to the
          // workflow list.
          openCreateWorkflowDialog({ source: 'command-palette' });
        },
      });
    }
    return actions;
  }, [workflowName, locationName, projectName, appName, handleRunClick]);

  // Handler when trigger dialog successfully runs the workflow
  const handleTriggerSuccess = useCallback((runId?: string) => {
    if (runId && workflowName) {
      // Reset agent/scope/tool state to avoid showing stale data from a previous run
      setAgentIterations({});
      setScopeIterations({});
      setToolActions(new Map());
      setPendingToolActionFetches(new Set());
      setToolActionFetchQueue([]);
      setToolActionFetchRequest(null);
      setScopeRepetitions(new Map());
      setPendingScopeRepetitionFetches(new Set());
      setSelectedScopeRepetitions({});
      setScopeRepetitionFetchRequest(null);

      navigateToRun(runId);
    } else {
      // No runId from trigger — check the streaming store.
      // In streaming mode the POST doesn't return a runId; it arrives via SSE.
      // Fast streams may already have a real runId by the time onSuccess fires.
      const { runId: streamRunId, status: streamStatus } = useStreamingRunStore.getState();
      const hasRealStreamRunId = streamRunId && streamRunId !== 'pending' && !streamRunId.startsWith('unknown-');

      if (hasRealStreamRunId) {
        // Fast stream: runId already available — navigate immediately
        navigateToRun(streamRunId);
      } else if (streamStatus === 'connecting' || streamStatus === 'streaming') {
        // Stream in progress but no runId yet — arm the subscriber
        pendingStreamingNavRef.current = true;
      } else {
        // Not streaming — fall back to opening the monitoring panel
        handleTabChange('monitoring');
      }
    }
  }, [workflowName, navigateToRun, handleTabChange]);

  handleTriggerSuccessRef.current = handleTriggerSuccess;

  // /submitDraftRun returns immediately and is not a streaming endpoint, so we
  // can't rely on the streaming-store subscriber the way Test Draft does. When
  // the response omits the `x-ms-workflow-run-id` header, poll the runs API
  // briefly and pick the run with the most recent startTime. The mutation's
  // onSuccess already invalidated the runs cache, so this hits the backend.
  const findLatestRunIdAfterMock = useCallback(async (): Promise<string | undefined> => {
    if (!projectName || !workflowName || !appName) return undefined;

    let token: string | null;
    try {
      token = await getOttoToken();
    } catch {
      return undefined;
    }
    if (!token) return undefined;

    const MAX_ATTEMPTS = 6;
    const DELAY_MS = 500;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      try {
        const runs = await fetchRuns({ projectName, appName, workflowName, accessToken: token });
        if (runs.length > 0) {
          const newest = runs.reduce((a, b) =>
            new Date(b.startTime).getTime() > new Date(a.startTime).getTime() ? b : a
          );
          if (newest?.id) return newest.id;
        }
      } catch {
        // Swallow and keep polling — the run may not have surfaced yet.
      }
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
      }
    }
    return undefined;
  }, [projectName, workflowName, appName, getOttoToken]);

  // Mock run handler passed to the Test dialog (used when the global mock
  // toggle is on). The dialog saves the draft first; here we submit the mock
  // overrides and, because /submitDraftRun may omit the run-id header, poll
  // the runs API to surface the latest run for navigation.
  const handleMockRun = useCallback(
    async (triggerName: string): Promise<{ success: boolean; error?: string; runId?: string }> => {
      const result = await triggerMockRunMutation.mutateAsync(triggerName);
      if (result.success && !result.runId) {
        const runId = await findLatestRunIdAfterMock();
        return { ...result, ...(runId ? { runId } : {}) };
      }
      return result;
    },
    [triggerMockRunMutation, findLatestRunIdAfterMock],
  );

  // Auto-navigate to the streaming run once a real runId arrives.
  // When pendingStreamingNavRef is set (by handleTriggerSuccess in streaming mode),
  // this effect watches the streaming store. Once the status becomes terminal
  // and a real runId is available, it clears stale state and navigates.
  useEffect(() => {
    const unsub = useStreamingRunStore.subscribe((state) => {
      if (!pendingStreamingNavRef.current) return;

      const hasRealRunId = state.runId && state.runId !== 'pending' && !state.runId.startsWith('unknown-');

      if (hasRealRunId) {
        // Navigate as soon as a real runId arrives — monitoring view handles active runs
        pendingStreamingNavRef.current = false;
        // Clear stale agent/scope/tool state
        setAgentIterations({});
        setScopeIterations({});
        setToolActions(new Map());
        setPendingToolActionFetches(new Set());
        setToolActionFetchQueue([]);
        setToolActionFetchRequest(null);
        setScopeRepetitions(new Map());
        setPendingScopeRepetitionFetches(new Set());
        setSelectedScopeRepetitions({});
        setScopeRepetitionFetchRequest(null);
        navigateToRun(state.runId!);
      } else if (state.status === 'aborted') {
        // User cancelled — just clear pending flag, no side effects
        pendingStreamingNavRef.current = false;
      } else if (state.status === 'completed' || state.status === 'error') {
        // Terminal without usable runId — fall back to monitoring panel
        pendingStreamingNavRef.current = false;
        handleTabChange('monitoring');
      }
    });
    return unsub;
  }, [navigateToRun, handleTabChange]);

  // Subscribe only to the action we use. Reading the whole agent chat store
  // here would re-render this large designer root on every streamed chat token
  // (isStreaming/streamingContent/messages update per token).
  const createNewSession = useAgentChatStore((s) => s.createNewSession);

  // Handler for starting a new chat - goes to monitoring view with empty chat
  const handleStartChat = useCallback(() => {
    // Create a new session in the agent chat store (clears any existing session)
    createNewSession();
    // Clear any selected run - we're starting fresh
    setSelectedRunId(null);
    setSelectedExecutionId(null);
    setSelectedNodeId(null);
    // Navigate to monitoring tab for new chat
    handleTabChange('monitoring');
  }, [createNewSession, handleTabChange]);

  // Handler for when a new run is created from the chat panel
  // This is called when the chat panel receives a contextId from the first message
  // Handle exiting debug mode - clears URL param
  const handleExitDebugMode = useCallback(() => {
    if (workflowName) {
      // Navigate back to the workflow URL without debug param
      navigate(workflowBasePath, { replace: true });
    }
  }, [workflowName, workflowBasePath, navigate]);

  // Panel state handlers for DesignerLayout
  const handleCanvasStateChange = useCallback((newState: PanelState) => {
    if (newState === 'maximized') {
      setCodePanelState('minimized');
    }
  }, []);

  const handleCodeStateChange = useCallback((newState: PanelState) => {
    setCodePanelState(newState);
  }, []);

  const handleFileChange = (file: ActiveFile) => {
    setActiveFile(file);
  };

  const formatDuration = (ms?: number): string =>
    canonicalFormatDuration(ms, { style: 'execution', fallback: '' });

  const formatDateTime = (timestamp?: string): string =>
    canonicalFormatDateTime(timestamp, 'short', { fallback: '' });

  // Derive run badge data from unfiltered runs (single-pass reduce, O(n))
  const runCount = unfilteredRuns?.length ?? 0;
  const latestRunStatus = useMemo(() => {
    if (!unfilteredRuns?.length) return undefined;
    return unfilteredRuns.reduce((latest, run) =>
      !latest || new Date(run.startTime).getTime() > new Date(latest.startTime).getTime() ? run : latest
    ).status;
  }, [unfilteredRuns]);

  const renderRunsContent = () => {
    // Always render the master-detail layout with sub-tabs so users can
    // switch to Trigger history even when there are no runs yet. The Runs
    // sidebar (WorkflowRunsPanel) renders its own loading/empty UI, and the
    // right-side detail panel has its own "Select a run" fallback below.
    const isTriggersMode = monitoringMode === 'triggers';
    return (
      <div className={styles.monitoringLayout}>
        {/* Left sidebar: run list (collapsible) */}
        {isRunListOpen && (
          <div className={styles.runListSidebar}>
            <div className={styles.monitoringSubTabs}>
              <TabList
                selectedValue={monitoringMode}
                onTabSelect={handleMonitoringTabSelect}
                size="small"
                appearance="subtle"
                aria-label="Monitoring view"
              >
                <Tab id="monitoring-runs-tab" value="runs" aria-controls="monitoring-runs-panel">
                  Runs
                </Tab>
                <Tab id="monitoring-triggers-tab" value="triggers" aria-controls="monitoring-triggers-panel">
                  Trigger history
                </Tab>
              </TabList>
            </div>
            <div
              className={styles.monitoringSubTabBody}
              role="tabpanel"
              id="monitoring-runs-panel"
              aria-labelledby="monitoring-runs-tab"
              hidden={isTriggersMode}
            >
              {!isTriggersMode && (
                <WorkflowRunsPanel
                  selectedRunId={selectedRunId}
                  selectedExecutionId={selectedExecutionId}
                  onSelectRun={handleSelectRun}
                  onSelectExecution={handleSelectExecution}
                  hideRunButton={isConversational}
                  onStartChat={isConversational ? handleStartChat : undefined}
                />
              )}
            </div>
            <div
              className={styles.monitoringSubTabBody}
              role="tabpanel"
              id="monitoring-triggers-panel"
              aria-labelledby="monitoring-triggers-tab"
              hidden={!isTriggersMode}
            >
              {isTriggersMode && <TriggerHistorySidebar />}
            </div>
          </div>
        )}
        {/* Right: monitoring detail or empty state */}
        <div className={styles.monitoringMain}>
          {!isLoading &&!hasEditPermission && (
            <MessageBar intent="warning" layout="singleline" className={styles.monitoringReadOnlyBanner}>
              <MessageBarBody>
                <ShieldLockRegular className={styles.readOnlyBannerIcon} aria-hidden="true" />
                <MessageBarTitle>Read-only</MessageBarTitle>
                You might not be able to view all run data for this workflow.
              </MessageBarBody>
            </MessageBar>
          )}
          {isTriggersMode ? (
            selectedTriggerHistoryId ? (
              <TriggerHistoryDetailPanel
                onViewRun={(runName) => {
                  setMonitoringMode('runs');
                  setSelectedTriggerHistoryId(null);
                  handleSelectRun(runName);
                }}
              />
            ) : (
              <div className={styles.monitoringEmptyDetail}>
                <HistoryRegular className={styles.monitoringEmptyIcon} aria-hidden="true" />
                <Text size={500} weight="semibold">Select a trigger evaluation</Text>
                <Text className={styles.monitoringEmptyText}>
                  Pick a row to see trigger inputs, outputs, and resubmit options.
                </Text>
              </div>
            )
          ) : selectedRun ? (
            <div className={styles.monitoringContainer}>
              {/* Run Info Header */}
              <div className={styles.runInfoHeader}>
                {runListToggleButton}
                <Divider vertical className={styles.dividerHeight} />
            <div className={styles.runInfoTitleWrap}>
              <Tooltip
                content={selectedExecution ? `Execution ${selectedExecution.id}` : `Run ${selectedRun.id}`}
                relationship="label"
              >
                <Text className={styles.runInfoTitle}>
                  {selectedExecution ? `Execution ${selectedExecution.id}` : `Run ${selectedRun.id}`}
                </Text>
              </Tooltip>
            </div>
            <Divider vertical className={styles.dividerHeight} />
            <span className={styles.statusBadgeFull}>
              <RunStatusBadge status={selectedExecution?.status ?? selectedRun.status} size="small" />
            </span>
            <span className={styles.statusBadgeCompact}>
              <RunStatusBadge status={selectedExecution?.status ?? selectedRun.status} compact />
            </span>
            <div className={styles.runInfoDetails}>
              <span>{formatDateTime(selectedExecution?.timestamp ?? selectedRun.startTime)}</span>
              {(selectedExecution?.duration ?? selectedRun.duration) && (
                <>
                  <span>•</span>
                  <span>{formatDuration(selectedExecution?.duration ?? selectedRun.duration)}</span>
                </>
              )}
            </div>
            {(() => {
              const overflowActions: RunAction[] = [];
              if (selectedRun.status === 'running' && hasEditPermission) {
                overflowActions.push({
                  id: 'cancel',
                  label: cancelMutation.isPending ? 'Cancelling…' : 'Cancel',
                  ariaLabel: 'Cancel run',
                  icon: <StopRegular />,
                  onClick: handleCancelRun,
                  disabled: cancelMutation.isPending,
                });
              }
              // Order in the cluster (left → right): Debug, Export, Generate Template,
              // Inspector, Rerun. Rerun sits at the trailing edge as the headline action.
              if (canDebugInEditor) {
                overflowActions.push({
                  id: 'debug',
                  label: 'Debug in Editor',
                  ariaLabel: 'Debug in editor',
                  icon: <BugRegular />,
                  onClick: handleDebugInEditor,
                });
              }
              overflowActions.push({
                id: 'export',
                label: 'Export',
                ariaLabel: 'Export run as JSON',
                icon: <ArrowDownloadRegular />,
                onClick: handleExportRun,
              });
              if (isGenerateTemplateEnabled && selectedRun.status === 'succeeded') {
                overflowActions.push({
                  id: 'generate-template',
                  label: isGeneratingRecipe ? 'Generating...' : 'Generate Template',
                  ariaLabel: 'Generate recipe template from this run',
                  icon: isGeneratingRecipe ? <Spinner size="extra-tiny" /> : <GridDotsRegular />,
                  onClick: handleGenerateTemplate,
                  disabled: isGeneratingRecipe,
                });
              }
              if (hasEditPermission) {
                overflowActions.push({
                  id: 'inspect',
                  label: 'Data Inspector',
                  // Toggle: aria-label reflects the action that the next
                  // click will perform, so screen readers say "Close" when
                  // the inspector is currently open. The tooltip mirrors
                  // the label for sighted users.
                  ariaLabel: isInspectorOpen ? 'Close Data Inspector' : 'Open Data Inspector',
                  tooltip: isInspectorOpen ? 'Close Data Inspector' : 'Open Data Inspector',
                  icon: <DataTreemapRegular />,
                  onClick: () => setIsInspectorOpen(prev => !prev),
                });
              }
              if (canRerunSelectedRun) {
                overflowActions.push({
                  id: 'rerun',
                  label: resubmitMutation.isPending ? 'Rerunning…' : 'Rerun',
                  ariaLabel: 'Rerun workflow',
                  icon: <ArrowRepeatAllRegular />,
                  onClick: handleResubmitRun,
                  disabled: resubmitMutation.isPending,
                });
              }
              return (
                <div className={styles.runInfoActions}>
                  {/* Refresh + timestamp + status live to the left of the
                      action cluster — they are utility / state, not
                      headline actions. */}
                  <Tooltip content="Refresh run data" relationship="label">
                    <Button
                      appearance="subtle"
                      icon={isRefreshing ? <Spinner size="extra-tiny" /> : <ArrowClockwiseRegular />}
                      onClick={handleRefreshRun}
                      disabled={isRefreshing}
                      aria-label="Refresh run data"
                      aria-busy={isRefreshing}
                      aria-describedby={refreshRelativeLabel ? 'refresh-timestamp' : undefined}
                      size="small"
                    >
                      <span className={styles.refreshButtonLabel}>{isRefreshing ? 'Refreshing…' : 'Refresh'}</span>
                    </Button>
                  </Tooltip>
                  {refreshRelativeLabel && (
                    <span className={styles.refreshTimestampSlot}>
                      <span className={styles.refreshTimestampGhost} aria-hidden="true">
                        Updated 59s ago
                      </span>
                      <Text id="refresh-timestamp" className={styles.refreshTimestamp}>
                        {refreshRelativeLabel}
                      </Text>
                    </span>
                  )}
                  <span
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    className={styles.visuallyHidden}
                  >
                    {refreshAnnouncement}
                  </span>
                  <Divider vertical className={mergeClasses(styles.dividerHeight, styles.dividerInlineMargin)} />
                  {/* Right cluster: every action renders inline at every width.
                      Labels collapse to icon-only at <1100px via the
                      `toolbarButtonLabel` container query; nothing is ever
                      pushed into a More menu — Approach 2. */}
                  <div className={styles.runInfoOverflowGroup}>
                    {overflowActions.map((a) => (
                      <Tooltip key={a.id} content={a.tooltip ?? a.label} relationship="label">
                        <Button
                          appearance={a.appearance ?? 'subtle'}
                          icon={a.icon as React.ReactElement}
                          onClick={a.onClick}
                          {...(a.disabled !== undefined ? { disabled: a.disabled } : {})}
                          aria-label={a.ariaLabel}
                          size="small"
                        >
                          <span className={styles.toolbarButtonLabel}>{a.label}</span>
                        </Button>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Canvas Area with optional Data Inspector */}
          <div className={styles.canvasWithInspector}>
            <div className={styles.canvasMain}>
              <WorkflowDesigner
                key={routeIdentityKey}
                isMonitoringMode={true}
                nodeExecutions={nodeExecutions}
                selectedNodeId={selectedNodeId}
                onNodeClick={handleNodeClick}
                isLoading={isRunLoading}
                workflowSnapshot={selectedRun?.workflowSnapshot}
                agentIterations={agentIterations}
                agentIterationsData={agentIterationsData}
                onIterationChange={handleIterationChange}
                scopeIterations={scopeIterations}
                onScopeIterationChange={handleScopeIterationChange}
              />
            </div>
            {isInspectorOpen && (
              <DataInspector
                nodeExecution={selectedNodeExecution}
                onClose={() => setIsInspectorOpen(false)}
              />
            )}
          </div>

          {/* Resizable Bottom Panel with Chat (for agentic) and Logs */}
          <div className={styles.bottomPanelWrapper}>
            <ResizablePanel
              direction="vertical"
              defaultSize={280}
              minSize={100}
              maxSize={500}
              resizerPosition="start"
            >
              <div className={styles.bottomPanelContent}>
                {isAnyAgentWorkflow && (
                  <div className={
                    isChatCollapsed
                      ? styles.chatPanelWrapper
                      : isLogsCollapsed
                        ? styles.chatPanelWrapperExpanded
                        : styles.chatPanelWrapper
                  }>
                    {isConversational ? (
                      isChatCollapsed ? (
                        <ChatPanel
                          agentUrl={agentUrlConfig?.agentUrl}
                          apiKey={agentUrlConfig?.apiKey}
                          developerKey={agentUrlConfig?.oboUserToken}
                          contextId={selectedRunId ?? undefined}
                          disabled={selectedRun?.status?.toLowerCase() !== 'running'}
                          isCollapsed={true}
                          onCollapseChange={handleChatCollapseChange}
                          onNewChat={handleStartChat}
                        />
                      ) : isLogsCollapsed ? (
                        <ChatPanel
                          agentUrl={agentUrlConfig?.agentUrl}
                          apiKey={agentUrlConfig?.apiKey}
                          developerKey={agentUrlConfig?.oboUserToken}
                          contextId={selectedRunId ?? undefined}
                          disabled={selectedRun?.status?.toLowerCase() !== 'running'}
                          isCollapsed={false}
                          onCollapseChange={handleChatCollapseChange}
                          onNewChat={handleStartChat}
                        />
                      ) : (
                        <ResizablePanel
                          direction="horizontal"
                          defaultSize={350}
                          minSize={250}
                          maxSize={600}
                          resizerPosition="end"
                        >
                          <ChatPanel
                            agentUrl={agentUrlConfig?.agentUrl}
                            apiKey={agentUrlConfig?.apiKey}
                            developerKey={agentUrlConfig?.oboUserToken}
                            contextId={selectedRunId ?? undefined}
                            disabled={selectedRun?.status?.toLowerCase() !== 'running'}
                            isCollapsed={false}
                            onCollapseChange={handleChatCollapseChange}
                            onNewChat={handleStartChat}
                          />
                        </ResizablePanel>
                      )
                    ) : (
                      isChatCollapsed ? (
                        <ChatLogPanel
                          chatHistory={chatHistory}
                          isLoading={isRunLoading}
                          isCollapsed={true}
                          onCollapseChange={handleChatCollapseChange}
                          onToolClick={handleNodeClick}
                          onIterationChange={handleIterationChange}
                          executionLogToolNames={executionLogToolNames}
                        />
                      ) : isLogsCollapsed ? (
                        <ChatLogPanel
                          chatHistory={chatHistory}
                          isLoading={isRunLoading}
                          isCollapsed={false}
                          onCollapseChange={handleChatCollapseChange}
                          onToolClick={handleNodeClick}
                          onIterationChange={handleIterationChange}
                          executionLogToolNames={executionLogToolNames}
                        />
                      ) : (
                        <ResizablePanel
                          direction="horizontal"
                          defaultSize={350}
                          minSize={250}
                          maxSize={600}
                          resizerPosition="end"
                        >
                          <ChatLogPanel
                            chatHistory={chatHistory}
                            isLoading={isRunLoading}
                            isCollapsed={false}
                            onCollapseChange={handleChatCollapseChange}
                            onToolClick={handleNodeClick}
                            onIterationChange={handleIterationChange}
                            executionLogToolNames={executionLogToolNames}
                          />
                        </ResizablePanel>
                      )
                    )}
                  </div>
                )}
                <div className={isLogsCollapsed ? styles.logsPanelWrapperCollapsed : styles.logsPanelWrapper}>
                  <LogsPanel
                    nodeExecutions={nodeExecutions}
                    selectedNodeExecution={selectedNodeExecution}
                    selectedNodeId={selectedNodeId}
                    onNodeSelect={handleNodeClick}
                    selectedIterationIndex={selectedToolIteration ?? undefined}
                    onIterationChange={handleIterationChange}
                    isLoadingRun={isRunLoading}
                    isLoadingDetails={isActionDetailsLoading}
                    isCollapsed={isLogsCollapsed}
                    onCollapseChange={handleLogsCollapseChange}
                    agentIterations={agentIterationsData}
                    loadingAgentIterations={loadingAgentIterations}
                    chatHistory={chatHistory}
                    toolActions={toolActions}
                    loadingToolActions={pendingToolActionFetches}
                    onFetchToolActions={handleFetchToolActions}
                    canvasAgentIterations={agentIterations}
                    scopeRepetitions={scopeRepetitions}
                    loadingScopeRepetitions={pendingScopeRepetitionFetches}
                    onFetchRepetitions={handleFetchScopeRepetitions}
                    onRepetitionSelect={handleRepetitionSelect}
                    selectedRepetitionName={Object.values(selectedScopeRepetitions)[0]}
                    scopeActions={scopeActions}
                    loadingScopeActions={pendingScopeActionFetches}
                    onFetchScopeActions={handleFetchScopeActions}
                    runId={selectedRunId ?? undefined}
                    canvasScopeIterations={scopeIterations}
                    chatMessageIterationActions={chatMessageIterationActions}
                    loadingChatMessageIterations={pendingChatMessageIterationFetches}
                    onFetchChatMessageIterationActions={handleFetchChatMessageIterationActions}
                    foundryPortalUrl={foundryPortalUrl}
                    isConversational={isConversational}
                    workflowDefinition={workflow ?? undefined}
                  />
                </div>
              </div>
            </ResizablePanel>
          </div>
            </div>
          ) : (
            <>
              {/* Mirror the selected-run header so the run-list toggle is always
                  reachable in the Monitoring view, not only when a run is open. */}
              <div className={styles.runInfoHeader}>
                {runListToggleButton}
              </div>
              <div className={styles.monitoringEmptyDetail}>
                <Text size={400} weight="semibold">Select a run</Text>
                <Text>Choose a run from the list to view execution details</Text>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderDesignerContent = () => {
    return (
      <div className={styles.designerWithChat}>
        <div className={styles.designerContent}>
          <DesignerLayout
            canvasState={canvasPanelState}
            codeState={codePanelState}
            onCanvasStateChange={handleCanvasStateChange}
            onCodeStateChange={handleCodeStateChange}
            activeFile={activeFile}
            onFileChange={handleFileChange}
            hasConnections={!!connections}
            onGenerateWorkflow={handleGenerateWorkflow}
            isChatSidebarOpen={isChatSidebarOpen}
            onToggleCode={handleToggleCode}
            onToggleChat={handleToggleChatSidebar}
            onRun={handleRunClick}
            isDirty={isDirty}
            isSaving={isSaving}
            onSave={save}
            isValidating={isSaveValidating || isProactiveValidating}
            isConversational={isConversational}
            onStartChat={handleStartChat}
            workflow={workflow}
            onExitDebugMode={handleExitDebugMode}
            draftModeEnabled={true}
            workflowName={workflowName ?? ''}
            routeIdentityKey={routeIdentityKey}
            onValidate={triggerValidation}
            validationErrorCount={validationErrorCount}
            isPermissionReadOnly={!hasEditPermission}
          />
        </div>
        {/* Assistant Panel - hidden for read-only users, independent of Code */}
        {hasEditPermission && (isChatSidebarOpen || isAssistantMinimized) && (
          isAssistantMinimized ? (
            <SidePanel
              activeTab="chat"
              onTabChange={handleExpandAssistant}
              onClose={handleCloseAssistant}
              chatContent={
                <ChatSidebar
                  isOpen={true}
                  workflowName={workflowName ?? ''}
                  hideHeader
                />
              }
              isMinimized={true}
              hasUnreadMessages={hasUnreadMessages}
            />
          ) : (
            <ResizablePanel
              direction="horizontal"
              defaultSize={400}
              minSize={300}
              maxSize={600}
              resizerPosition="start"
            >
              <SidePanel
                activeTab="chat"
                onTabChange={() => {}}
                onClose={handleCloseAssistant}
                chatContent={
                  <ChatSidebar
                    isOpen={true}
                    workflowName={workflowName ?? ''}
                  />
                }
                onMinimize={handleMinimizeAssistant}
                hasUnreadMessages={hasUnreadMessages}
              />
            </ResizablePanel>
          )
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper} ref={contentWrapperRef}>
        <WorkflowViewTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          runCount={runCount}
          {...(latestRunStatus != null && { latestRunStatus })}
          workflowDisplayName={workflowName ? extractWorkflowName(workflowName) : ''}
          {...(workflowName && { workflowName })}
          onValidate={triggerValidation}
          validationErrorCount={validationErrorCount}
          isValidating={isProactiveValidating || isSaveValidating}
          {...(locationName && projectName && appName
            ? {
                onBack: () =>
                  navigate(paths.appWorkflows(locationName, projectName, appName, tenantId), {
                    replace: true,
                  }),
                backLabel: 'Back to workflows',
              }
            : {})}
        />
        {activeTab === 'designer' ? (
          <div
            className={styles.content}
            role="tabpanel"
            aria-labelledby="tab-designer"
            id="tabpanel-designer"
          >
            {renderDesignerContent()}
          </div>
        ) : (
          <div
            className={styles.content}
            role="tabpanel"
            aria-labelledby="tab-monitoring"
            id="tabpanel-monitoring"
          >
            {renderRunsContent()}
          </div>
        )}

        {/* Create Recipe Dialog — opened by "Generate Template" on the run toolbar */}
        <CreateRecipeDialog
          open={!!generatedRecipe}
          recipe={generatedRecipe}
          onDismiss={() => setGeneratedRecipe(null)}
        />

        {/* Trigger Workflow Dialog - opened from Run button on toolbar */}
        <TriggerWorkflowDialog
          open={isTriggerDialogOpen}
          onClose={() => setIsTriggerDialogOpen(false)}
          onSuccess={handleTriggerSuccess}
          {...(isDraftMode && { onSaveDraft: handleSaveDraft })}
          {...(isDraftMode && { onMockRun: handleMockRun })}
          {...(isDraftMode && { onStreamingDraftTrigger: handleStreamingDraftTrigger })}
          {...(workflow?.definition?.triggers && {
            workflowTriggers: workflow.definition.triggers as Record<string, { type: string; kind?: string; inputs?: Record<string, unknown> }>,
          })}
        />

        {/* Command palette extras — registers Actions + Ask AI prompts +
            free-form submit handler with the single GlobalNavPalette
            mounted in RootLayout. The palette handles open state, keyboard,
            and rendering; we just feed it route-specific data. */}
        <PaletteExtensions.Register
          actions={paletteActions}
          askAi={askAi}
          onAskSubmit={handleCommandPaletteMessage}
          resetOnDeps={[paletteActions, askAi, handleCommandPaletteMessage]}
        />

        {/* Esc-to-close-assistant lives here (not in the main keyboard
            useEffect) so the palette open-state subscription doesn't
            re-render the entire designer subtree on every ⌘K toggle. */}
        <ChatSidebarEscapeHandler
          enabled={activeTab === 'designer'}
          isChatSidebarOpen={isChatSidebarOpen}
          onClose={handleCloseAssistant}
        />
      </div>
    </div>
  );
};

type WorkflowDesignerWithCodeProps = {
  /** Initial run ID from URL deep link */
  initialRunId?: string | undefined;
  /** Initial debug run ID from URL query param - loads execution into editor */
  initialDebugRunId?: string | undefined;
};

export const WorkflowDesignerWithCode = ({ initialRunId, initialDebugRunId }: WorkflowDesignerWithCodeProps) => {
  return (
    <ConnectorProvider>
      <WorkflowDesignerContent initialRunId={initialRunId} initialDebugRunId={initialDebugRunId} />
    </ConnectorProvider>
  );
};
